// ═══════════════════════════════════════════════════════════════════
// Deterministic Ratings Engine — v3 (per-category dispatch)
// ───────────────────────────────────────────────────────────────────
// Each product CATEGORY has its own dedicated scoring algorithm for
// Reliability / Abuse Tolerance / Build Quality, because the physics
// that determines durability for a subwoofer (cone, thermal, motor)
// is fundamentally different from an amplifier (thermal headroom,
// PSU coherence, impedance stability) which is different again from
// an alternator (rotor/stator copper, diode bridge, idle delivery).
//
// Dispatch lives in computeReliability/AbuseTolerance/BuildQuality —
// each switches on p.category and delegates to a category-specific
// scorer that knows that domain's failure modes intimately.
//
// Live user-review scoring uses Bayesian shrinkage (low-volume
// products are pulled toward a category prior so 3-review products
// can't outrank 200-review ones by accident).
// ═══════════════════════════════════════════════════════════════════

import type { Product, ProductCategory } from "@/data/productDatabase";
import { getBenchTest } from "@/data/benchTestData";

export interface ScoreFactor {
  label: string;
  delta: number;
  detail?: string;
}

export interface ComputedRating {
  score: number;
  baseline: number;
  factors: ScoreFactor[];
  summary: string;
}

export interface ProductRatings {
  reliability: ComputedRating;
  abuseTolerance: ComputedRating;
  buildQuality: ComputedRating;
}

// ─── Brand reputation table ────────────────────────────────────────
// Tier reflects long-term failure data, QC consistency, and engineering
// over-build (rated specs vs measured). Range 0-100 internally; later
// scaled per-metric so it never single-handedly dominates a score.
const BRAND_REPUTATION: Record<string, number> = {
  // Tier S — measured-over-rated, world-class QC
  "JL Audio": 95, "Focal": 94, "Hertz": 93, "Morel": 93,
  "Audiofrog": 96, "Mosconi": 96, "Brax": 97, "Helix": 93,
  "Rockford Fosgate": 92, "Audison": 91,
  // Tier A — strong daily-driver / SPL pedigree
  "Sundown Audio": 88, "DD Audio": 87, "Image Dynamics": 88,
  "Hybrid Audio": 87, "Illusion Audio": 87,
  "Memphis Audio": 82, "Kicker": 82, "Alpine": 84,
  "Mechman": 90, "Singer Alternators": 92, "Singer": 92,
  "DC Power Engineering": 90, "Iraggi": 88, "Excessive Amperage": 89,
  "Ohio Generator": 87, "Nations Starter & Alternator": 86,
  "XS Power": 85, "Northstar Battery": 86,
  "Soundqubed": 84, "Soundstream": 78,
  // Boutique / enthusiast-respected SPL & SQ brands
  // (heavy reputation on DIYMA, SMD Forum, CACO, r/CarAV — measured-above-rated)
  "Stereo Integrity": 90, "SSA": 86, "Sound Solutions Audio": 86,
  "Ascendant Audio": 85, "Audio Dynamics": 84, "Gately Audio": 84,
  "Ground Zero": 88, "Audiomobile": 88,
  "Crossfire Audio": 80, "Exile Audio": 80, "Sky High Car Audio": 80,
  "Nemesis Audio": 78, "Addictive Audio": 78, "Meso Audio": 78,
  "Obsidian Audio": 80, "Wolfram Audio": 80, "Nightshade Audio": 78,
  // Pro-audio / DIY drivers (respected engineering, modest car-audio mindshare)
  "Beyma": 88, "Peerless": 85, "Dayton Audio": 82, "Tang Band": 82,
  "PSI Audio": 86, "PRV Audio": 80, "B&C Speakers": 92,
  // Tier B — solid value, occasional QC inconsistency
  "Kenwood": 75, "Pioneer": 76, "JBL": 75, "Polk": 74, "Polk Audio": 74,
  "Massive Audio": 74, "Sony": 72, "Infinity": 72,
  "DC Audio": 78, "Ampere Audio": 78, "Crescendo Audio": 76,
  "Taramps": 76, "Soundigital": 78, "Stetsom": 75,
  "B2 Audio": 76, "Fi Audio": 80, "Resilient Sounds": 76,
  "Incriminator Audio": 76, "Audioque": 78, "Down4Sound": 80,
  "Skar Audio": 72, "American Bass": 72, "Deaf Bonce": 74,
  "Phoenix Gold": 70, "Precision Power": 72,
  "Earthquake Sound": 70, "Timpano": 72,
  "Rockville": 60, "Planet Audio": 50, "Audiopipe": 64,
  // Tier C — budget, higher variance
  "CT Sounds": 64, "NVX": 66, "DS18": 58, "Cerwin Vega": 62,
  "MTX": 62, "MTX Audio": 62, "Hifonics": 58, "Orion": 62, "Cadence": 56,
  "RE Audio": 64,
  // Tier D — chronic QC issues / inflated specs
  "Power Acoustik": 38, "Boss Audio": 24, "Pyle": 18, "Lanzar": 26,
};
const BRAND_DEFAULT = 50;

// ─── Calibration overrides ─────────────────────────────────────────
let BRAND_OVERRIDES: Record<string, number> = {};
const OVERRIDES_STORAGE_KEY = "ratings.brandOverrides.v1";
if (typeof window !== "undefined") {
  try {
    const raw = window.localStorage.getItem(OVERRIDES_STORAGE_KEY);
    if (raw) BRAND_OVERRIDES = JSON.parse(raw) ?? {};
  } catch { /* ignore */ }
}
export function getBrandOverrides(): Record<string, number> { return { ...BRAND_OVERRIDES }; }
export function setBrandOverrides(next: Record<string, number>, persist = true) {
  BRAND_OVERRIDES = { ...next };
  if (persist && typeof window !== "undefined") {
    try { window.localStorage.setItem(OVERRIDES_STORAGE_KEY, JSON.stringify(BRAND_OVERRIDES)); } catch { /* ignore */ }
  }
}
export function clearBrandOverrides() {
  BRAND_OVERRIDES = {};
  if (typeof window !== "undefined") {
    try { window.localStorage.removeItem(OVERRIDES_STORAGE_KEY); } catch { /* ignore */ }
  }
}
export function getDefaultBrandReputation(): Record<string, number> { return { ...BRAND_REPUTATION }; }

function brandRep(brand: string): number {
  return BRAND_OVERRIDES[brand] ?? BRAND_REPUTATION[brand] ?? BRAND_DEFAULT;
}
function brandTierLabel(score: number): string {
  if (score >= 90) return "Tier S";
  if (score >= 80) return "Tier A";
  if (score >= 65) return "Tier B";
  if (score >= 50) return "Tier C";
  return "Tier D";
}

// ─── Numeric helpers ───────────────────────────────────────────────
function num(v: string | number | undefined | null): number {
  if (v === undefined || v === null) return 0;
  if (typeof v === "number") return v;
  const m = v.toString().replace(/,/g, "").match(/-?\d+(\.\d+)?/);
  return m ? parseFloat(m[0]) : 0;
}
function clamp01(n: number) { return Math.max(0, Math.min(1, n)); }
function clamp100(n: number) { return Math.max(0, Math.min(100, Math.round(n))); }

// Smooth diminishing-returns curve. `half` = value at which output = maxPts/2.
function smoothUp(value: number, half: number, maxPts: number): number {
  if (value <= 0 || half <= 0) return 0;
  return (maxPts * value) / (value + half);
}
// Piecewise-linear S-curve between two breakpoints.
function ramp(value: number, lo: number, hi: number, minPts: number, maxPts: number): number {
  if (value <= lo) return minPts;
  if (value >= hi) return maxPts;
  return minPts + (maxPts - minPts) * ((value - lo) / (hi - lo));
}
function pushFactor(factors: ScoreFactor[], delta: number, label: string, detail?: string) {
  if (Math.abs(delta) < 0.5) return;
  factors.push({ label, delta: Math.round(delta), detail });
}

// ─── Amplifier-specific parsers ────────────────────────────────────
function parseWatts(raw: unknown): number {
  if (raw === undefined || raw === null) return 0;
  const s = String(raw).replace(/,/g, "").toLowerCase();
  const mx = s.match(/(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)/);
  if (mx) return parseFloat(mx[1]) * parseFloat(mx[2]);
  const m = s.match(/-?\d+(\.\d+)?/);
  return m ? parseFloat(m[0]) : 0;
}
function totalAmpRms(specs: Record<string, string | number | undefined>): number {
  const candidates: number[] = [];
  for (const [k, v] of Object.entries(specs)) {
    if (!/^rms\b|^bridged\b|^power\b/i.test(k)) continue;
    const w = parseWatts(v); if (w > 0) candidates.push(w);
  }
  return candidates.length ? Math.max(...candidates) : 0;
}
function totalFuseAmps(raw: unknown): number { return parseWatts(raw); }

// Parse all "RMS @ Nohm" entries — returns sorted [{ohm, watts}, ...].
function ampImpedanceLadder(specs: Record<string, string | number | undefined>): Array<{ ohm: number; watts: number }> {
  const rows: Array<{ ohm: number; watts: number }> = [];
  for (const [k, v] of Object.entries(specs)) {
    const m = k.match(/(?:rms|bridged)\s*@?\s*(\d+(?:\.\d+)?)\s*(?:o?h?m|ohms?)/i);
    if (!m) continue;
    const w = parseWatts(v);
    if (w > 0) rows.push({ ohm: parseFloat(m[1]), watts: w });
  }
  return rows.sort((a, b) => a.ohm - b.ohm);
}
function parseFreqResponse(raw: unknown): { low: number; high: number } | null {
  if (!raw) return null;
  const s = String(raw).toLowerCase().replace(/,/g, "");
  const m = s.match(/(\d+(?:\.\d+)?)\s*hz?\s*[-–to]+\s*(\d+(?:\.\d+)?)\s*(k?)hz?/);
  if (!m) return null;
  const low = parseFloat(m[1]);
  const high = parseFloat(m[2]) * (m[3] === "k" ? 1000 : 1);
  return { low, high };
}

// ═════════════════════════════════════════════════════════════════
// Cross-cutting helpers: bench-test overlay, topology-aware
// watts-per-pound, tweeter FR-first scoring, positive-silence signal
// ═════════════════════════════════════════════════════════════════

/** Detect amp topology class. */
export type AmpTopologyClass = "class-d-smps" | "class-ab-linear" | "class-gh" | "unknown";
export function ampTopologyClass(specs: Record<string, string | number | undefined>): AmpTopologyClass {
  const raw = ((specs["Topology"] ?? specs["Class"] ?? "") + " " + (specs["PSU"] ?? specs["Power Supply"] ?? "")).toString().toLowerCase();
  if (/class[-\s]?d\b|full[-\s]?range d\b|monoblock d|switching/.test(raw) && !/a\/?b/.test(raw)) return "class-d-smps";
  if (/class[-\s]?g|class[-\s]?h/.test(raw)) return "class-gh";
  if (/a\/?b|linear/.test(raw)) return "class-ab-linear";
  // Fallback heuristic — high efficiency + monoblock ≈ Class-D
  const eff = num(specs["Efficiency"]);
  const ch = num(specs["Channels"]) || 1;
  if (eff >= 82 || ch === 1) return "class-d-smps";
  return "unknown";
}

/**
 * Topology-aware watts/lb plausibility bands.
 * Modern Class-D + SMPS legitimately achieves 300–500 W/lb; linear-PSU
 * A/B tops out around 120 W/lb before physics disagrees. We evaluate
 * each amp against its own topology class, never against a single
 * universal threshold. Returns { verdict, delta, label, detail } so the
 * scorer can push a factor with human-readable context.
 */
export function wppPlausibility(
  power: number,
  weight: number,
  topology: AmpTopologyClass,
  fuseAmps: number,
): { verdict: "generous" | "healthy" | "aggressive" | "improbable"; delta: number; label: string; detail: string } | null {
  if (power <= 0 || weight <= 0) return null;
  const wpp = power / weight;
  // Topology-specific plausible ceilings (measured across modern reference amps).
  const ceilings: Record<AmpTopologyClass, { generous: number; healthy: number; aggressive: number; improbable: number }> = {
    "class-d-smps":   { generous: 150, healthy: 300, aggressive: 500, improbable: 800 },
    "class-gh":       { generous: 100, healthy: 200, aggressive: 350, improbable: 550 },
    "class-ab-linear":{ generous:  70, healthy: 130, aggressive: 220, improbable: 350 },
    "unknown":        { generous: 120, healthy: 240, aggressive: 400, improbable: 650 },
  };
  const c = ceilings[topology];
  // Fuse cross-check — an "improbable" wpp is only truly improbable if
  // fuse rating also can't sustain the claim. A big amp with the right
  // fuse gets the benefit of the doubt.
  const fuseSupports = fuseAmps > 0 ? (fuseAmps * 13.8 * 0.85) >= power * 0.6 : true;
  if (wpp < c.generous) {
    return { verdict: "generous", delta: 5, label: "Generous mass per rated watt",
      detail: `${weight} lbs for ${power.toLocaleString()}W (${Math.round(wpp)} W/lb) — comfortably below the ${topology.replace(/-/g," ")} class ceiling.` };
  }
  if (wpp < c.healthy) {
    return { verdict: "healthy", delta: 2, label: "Healthy build density",
      detail: `${Math.round(wpp)} W/lb is normal for ${topology.replace(/-/g," ")} designs.` };
  }
  if (wpp < c.aggressive) {
    return { verdict: "aggressive", delta: 0, label: "Aggressive but topology-consistent",
      detail: `${Math.round(wpp)} W/lb is high for the class but achievable with modern SMPS + tight PCB layout.` };
  }
  if (wpp < c.improbable && fuseSupports) {
    return { verdict: "aggressive", delta: -2, label: "Very aggressive W/lb",
      detail: `${Math.round(wpp)} W/lb is near the physical ceiling for ${topology.replace(/-/g," ")} — sustained abuse likely derates.` };
  }
  if (!fuseSupports) {
    return { verdict: "improbable", delta: -8, label: "W/lb inconsistent with fuse rating",
      detail: `${Math.round(wpp)} W/lb combined with ${fuseAmps || "no"}A fuse cannot sustain the claim — rating likely peak-only.` };
  }
  return { verdict: "improbable", delta: -6, label: "Physically improbable W/lb for topology",
    detail: `${Math.round(wpp)} W/lb exceeds what ${topology.replace(/-/g," ")} thermal + PSU mass allows.` };
}

/**
 * Bench-test overlay: if independent measurements exist, prefer them
 * and return a factor that flags the discrepancy. Positive signal for
 * "measured meets or exceeds spec"; negative for material shortfalls.
 */
export function benchTestFactor(p: Product): { delta: number; label: string; detail: string } | null {
  const bench = getBenchTest(p.id);
  if (!bench) return null;
  const specs = p.specs as Record<string, string | number | undefined>;
  const notes: string[] = [];
  let delta = 4; // presence of independent measurement is itself trust-positive

  // Amps: RMS vs rated
  if (bench.measuredRmsWatts && bench.measuredRmsWatts > 0) {
    const rated = totalAmpRms(specs);
    if (rated > 0) {
      const ratio = bench.measuredRmsWatts / rated;
      if (ratio >= 1.02) { delta += 5; notes.push(`${bench.measuredRmsWatts}W measured vs ${rated}W rated (over-delivers)`); }
      else if (ratio >= 0.95) { delta += 3; notes.push(`${bench.measuredRmsWatts}W measured matches rated`); }
      else if (ratio >= 0.85) { delta -= 3; notes.push(`measured ${Math.round((1 - ratio) * 100)}% below rated`); }
      else { delta -= 10; notes.push(`measured ${Math.round((1 - ratio) * 100)}% below rated — inflated spec`); }
    }
  }
  if (bench.sustainedLoadHoldPct !== undefined) {
    if (bench.sustainedLoadHoldPct >= 90) { delta += 4; notes.push(`holds ${bench.sustainedLoadHoldPct}% of RMS for 30 min sustained`); }
    else if (bench.sustainedLoadHoldPct >= 80) { delta += 1; notes.push(`${bench.sustainedLoadHoldPct}% sustained-load hold`); }
    else { delta -= 6; notes.push(`derates to ${bench.sustainedLoadHoldPct}% under sustained load — SPL burst amp`); }
  }
  // Subs: Xmax vs rated
  if (bench.measuredXmaxMm !== undefined) {
    const ratedXmax = num(specs["Xmax"]);
    if (ratedXmax > 0) {
      const ratio = bench.measuredXmaxMm / ratedXmax;
      if (ratio >= 0.95) { delta += 3; notes.push(`Klippel Xmax ${bench.measuredXmaxMm}mm matches rated`); }
      else if (ratio >= 0.85) { delta -= 3; notes.push(`Klippel Xmax ${bench.measuredXmaxMm}mm vs ${ratedXmax}mm rated`); }
      else { delta -= 8; notes.push(`Klippel Xmax ${bench.measuredXmaxMm}mm is ${Math.round((1 - ratio) * 100)}% below rated`); }
    }
  }
  // Speakers/subs/tweeters: sensitivity vs rated (±1 dB tolerance)
  if (bench.measuredSensitivityDb !== undefined) {
    const rated = num(specs["Sensitivity"]);
    if (rated > 0 && Math.abs(bench.measuredSensitivityDb - rated) > 1.5) {
      delta -= 3;
      notes.push(`sensitivity measures ${bench.measuredSensitivityDb} dB vs ${rated} dB rated`);
    }
  }
  if (bench.notes) notes.push(bench.notes);
  return { delta, label: `Independent bench data (${bench.source})`, detail: notes.join("; ") };
}

/**
 * Tweeter performance score — FR-flatness FIRST, breakup-vs-crossover
 * SECOND, then power-compression and off-axis proxies. Material is
 * only used as a tie-breaker between two tweeters that score equally
 * on the primary criteria (see tweeterMaterialTiebreak).
 *
 * Priority order per spec:
 *   1. FR deviation ±dB across usable bandwidth (flatter = better)
 *   2. Breakup mode frequency vs crossover point
 *   3. Power compression proxy (from bench or brand-implied)
 *   4. Off-axis dispersion consistency (from dispersion spec if published)
 */
export function tweeterPerformanceScore(p: Product): { score: number; factors: ScoreFactor[] } {
  const specs = p.specs as Record<string, string | number | undefined>;
  const factors: ScoreFactor[] = [];
  let score = 50;

  const bench = getBenchTest(p.id);
  const publishedFr = parseFreqResponse(
    specs["Frequency Response"] ?? specs["Freq Response"] ?? specs["FR"],
  );
  const crossoverHz = num(specs["Crossover Point"] ?? specs["Crossover"] ?? specs["Recommended Crossover"]) || 3500;

  // (1) FR flatness — dominant signal. Prefer measured, fall back to
  // published FR width as a soft proxy (wider, well-centered response
  // usually correlates with flatter response for tweeters).
  const measuredDev = bench?.frDeviationDb;
  if (measuredDev !== undefined) {
    // Klippel/anechoic ±dB
    if (measuredDev <= 1.5)      { pushFactor(factors, 18, `Measured FR ±${measuredDev} dB`, "Flatter than the vast majority of car-audio tweeters — reference-tier."); score += 18; }
    else if (measuredDev <= 2.5) { pushFactor(factors, 12, `Measured FR ±${measuredDev} dB`, "Well-behaved response with no obvious peaks."); score += 12; }
    else if (measuredDev <= 3.5) { pushFactor(factors, 4,  `Measured FR ±${measuredDev} dB`, "Some ripple present but manageable with passive EQ."); score += 4; }
    else                          { pushFactor(factors, -8, `Measured FR ±${measuredDev} dB`, "Audible peaks — will sound harsh or sibilant without careful EQ."); score -= 8; }
  } else if (publishedFr) {
    // Softer proxy: usable bandwidth extension
    if (publishedFr.high >= 25000 && publishedFr.low <= 2000) { pushFactor(factors, 8, "Wide published FR (2k–25k+ Hz)", "Broad usable bandwidth typically correlates with damped, flat response."); score += 8; }
    else if (publishedFr.high >= 22000) { pushFactor(factors, 4, "Extended top-end FR"); score += 4; }
    else if (publishedFr.high < 18000)   { pushFactor(factors, -4, `Limited top-end (${publishedFr.high} Hz)`); score -= 4; }
  }

  // (2) Breakup mode above crossover — the higher above Fx, the more
  // audibly clean. Well-executed silk/metal can beat poor beryllium.
  if (bench?.breakupFrequencyHz) {
    const octavesAboveXo = Math.log2(bench.breakupFrequencyHz / Math.max(1000, crossoverHz));
    if (bench.breakupFrequencyHz >= 30000)      { pushFactor(factors, 14, `Breakup at ${(bench.breakupFrequencyHz / 1000).toFixed(0)} kHz — well above audible band`, `${octavesAboveXo.toFixed(1)} octaves above the ${crossoverHz} Hz crossover.`); score += 14; }
    else if (bench.breakupFrequencyHz >= 22000) { pushFactor(factors, 8,  `Breakup at ${(bench.breakupFrequencyHz / 1000).toFixed(0)} kHz`, "Above 20 kHz — inaudible to almost all listeners."); score += 8; }
    else if (bench.breakupFrequencyHz >= 18000) { pushFactor(factors, 2,  `Breakup at ${(bench.breakupFrequencyHz / 1000).toFixed(0)} kHz`, "Marginal — young ears may hear it."); score += 2; }
    else                                          { pushFactor(factors, -8, `Breakup at ${(bench.breakupFrequencyHz / 1000).toFixed(0)} kHz`, "Rigid-piston limit inside the audible band — expect harshness."); score -= 8; }
  }

  // (3) Power compression proxy — voice-coil size + published RMS/peak
  // ratio tells us how much sustained power the dome can absorb before
  // Le(x) modulation and thermal compression flatten the top-end.
  const rms = num(specs["RMS Power"]);
  const vcSize = (specs["Voice Coil"] ?? "").toString().toLowerCase();
  const vcMm = /(\d+(?:\.\d+)?)\s*mm/.test(vcSize) ? parseFloat(vcSize.match(/(\d+(?:\.\d+)?)\s*mm/)![1]) : 0;
  if (rms > 0 && vcMm >= 28) { pushFactor(factors, 4, `${vcMm}mm voice coil handles ${rms}W`, "Large-format tweeter — resists power compression at concert-level SPL."); score += 4; }
  else if (rms >= 80) { pushFactor(factors, 3, `${rms}W RMS handling`, "Above-average power handling reduces thermal compression."); score += 3; }
  else if (rms > 0 && rms < 30) { pushFactor(factors, -2, `${rms}W RMS handling`, "Limited headroom — may compress under high-SPL peaks."); score -= 2; }

  // (4) Off-axis dispersion proxy — dome diameter and waveguide.
  const dome = (specs["Tweeter Type"] ?? specs["Dome"] ?? specs["Tweeter"] ?? "").toString().toLowerCase();
  if (/waveguide|horn/.test(dome)) { pushFactor(factors, 2, "Waveguide/horn loading", "Controlled directivity — reduces reflections in a car cabin."); score += 2; }
  if (/dome/.test(dome) && !/horn/.test(dome)) { pushFactor(factors, 2, "Wide-dispersion dome", "Broader off-axis response than horns — better staging for stock tweeter locations."); score += 2; }

  return { score: clamp100(score), factors };
}

/**
 * Material tiebreaker only. Never call as a primary scoring signal —
 * use tweeterPerformanceScore() instead. Returns 0..3 points to break
 * ties between tweeters that scored within 2 points of each other on
 * the primary FR/breakup/compression/dispersion criteria.
 */
export function tweeterMaterialTiebreak(p: Product): number {
  const dome = ((p.specs["Tweeter Type"] ?? p.specs["Dome"] ?? p.specs["Tweeter"]) ?? "").toString().toLowerCase();
  if (/beryllium/.test(dome)) return 3;
  if (/diamond/.test(dome)) return 3;
  if (/silk|textile/.test(dome)) return 2;
  if (/soft dome/.test(dome)) return 2;
  if (/aluminum|titanium|magnesium/.test(dome)) return 1;
  return 0;
}

/**
 * "Positive silence" — a product with high install base but low
 * complaint volume is more trustworthy than one with no data at all.
 * Forum sentiment is inherently biased toward failure reports since
 * satisfied long-term owners rarely post; this counter-weights that.
 */
export function positiveSilenceBonus(p: Product): { delta: number; label: string; detail: string } | null {
  const evidence = [
    p.evidence?.reliability, p.evidence?.abuseTolerance,
    p.evidence?.buildQuality, p.evidence?.userReviews,
  ].filter(Boolean).join(" ").toLowerCase();

  // Count failure-mode mentions — the lower, the more "silent" the install base.
  const failureMentions = [
    "burnout", "failure", "fried", "dies", "doa", "warranty queen",
    "melts", "comeback", "protection mode", "recurring", "widespread",
    "many users report", "commonly fails",
  ].reduce((n, k) => n + (evidence.includes(k) ? 1 : 0), 0);

  const base: Record<NonNullable<Product["installBase"]>, number> = {
    flagship: 6,
    mainstream: 3,
    niche: 0,
  };
  const tier = p.installBase;
  if (!tier) return null;
  // Reduce bonus by 2 points per failure mention (bottom out at 0).
  const bonus = Math.max(0, base[tier] - failureMentions * 2);
  if (bonus <= 0) return null;
  return {
    delta: bonus,
    label: `Positive silence — ${tier} install base, low complaint volume`,
    detail: "Long production run + high sales volume with few documented failure threads is a stronger reliability signal than positive reviews alone.",
  };
}

// ═════════════════════════════════════════════════════════════════
//                        AMPLIFIER ALGORITHM
// ─────────────────────────────────────────────────────────────────
// Failure modes that matter for amps:
//   • Thermal runaway → topology, efficiency, weight-per-watt, eff_headroom
//   • PSU starvation → fuse-vs-RMS coherence, impedance doubling pattern
//   • FET / output-stage failure → low-Z stability, bridged stability
//   • Inflated power claims → fuse/weight/impedance-ladder consistency
// ═════════════════════════════════════════════════════════════════

function ampReliability(p: Product): ComputedRating {
  const factors: ScoreFactor[] = [];
  const baseline = 58;
  let score = baseline;
  const specs = p.specs as Record<string, string | number | undefined>;

  // Brand — strongest single predictor of long-term amp reliability
  const brand = brandRep(p.brand);
  const brandDelta = (brand - 50) * 0.5;
  pushFactor(factors, brandDelta, `Brand track record: ${p.brand} (${brandTierLabel(brand)})`,
    "Long-term amp failure rates tracked across Crutchfield, SMD Forum, and owner data on Sundown/Soundqubed/Taramps platforms.");
  score += brandDelta;

  // Warranty
  const wYears = num(specs["Warranty"]);
  if (wYears >= 5) {
    pushFactor(factors, 8, `${wYears}-year warranty`,
      "5+ year coverage on an amp is rare — strong signal the manufacturer trusts the thermal & PSU design.");
    score += 8;
  } else if (wYears >= 2) {
    pushFactor(factors, 5, `${wYears}-year warranty`,
      "Above the 1y industry default — manufacturer stands behind the design.");
    score += 5;
  } else if (wYears > 0 && wYears < 1) {
    pushFactor(factors, -4, "Limited / 90-day warranty", "Short coverage on a heat-stressed product is a red flag.");
    score -= 4;
  }

  // Topology & efficiency — assume sane defaults so amps without published eff aren't punished
  const topology = (specs["Topology"] ?? specs["Class"] ?? "").toString().toLowerCase();
  const eff = num(specs["Efficiency"]);
  const isClassD = topology.includes("d") && !topology.includes("ab");
  const isFullRange = topology.includes("ab") || topology.includes("a/b") || topology.includes("gh");
  const assumedEff = eff > 0 ? eff : isClassD ? 85 : isFullRange ? 65 : 75;

  if (eff > 0) {
    const effPts = ramp(eff, 70, 92, -2, 10);
    pushFactor(factors, effPts, `${eff}% efficiency`,
      "Higher efficiency = less waste heat across thousands of hours; the #1 long-term reliability driver for amps.");
    score += effPts;
  }
  if (isClassD) {
    pushFactor(factors, 5, "Class-D topology", "Class-D runs 20-30degC cooler than A/B under identical output — far fewer thermal failures.");
    score += 5;
  } else if (isFullRange) {
    // Don't punish A/B — it has SQ advantages; just don't reward thermal efficiency
    pushFactor(factors, 1, "Class-A/B topology", "Higher heat output but proven linear topology favored by SQ installs.");
    score += 1;
  }

  // CEA-2006 compliance / dyno or birth-sheet — measured-over-rated certainty
  const certs = JSON.stringify(specs).toLowerCase();
  if (/cea[-\s]?2006|cea compliant/.test(certs)) {
    pushFactor(factors, 5, "CEA-2006 compliant", "Power rated under an independent standard — actual output meets or exceeds spec.");
    score += 5;
  }
  if (/birth sheet|dyno tested|dyno sheet|individually tested/.test(certs)) {
    pushFactor(factors, 5, "Dyno / birth-sheet tested", "Every unit individually verified before shipping — vanishingly rare DOA rate.");
    score += 5;
  }

  // Regulated PSU — keeps output stable as battery voltage sags during hard play
  const psu = (specs["Power Supply"] ?? specs["PSU"] ?? "").toString().toLowerCase();
  if (/regulated|smps|switching/.test(psu) || /regulated/.test(certs)) {
    pushFactor(factors, 4, "Regulated power supply",
      "Output stays flat as system voltage sags from 14.4V to 11V — protects FETs from PSU sag-induced clipping.");
    score += 4;
  }

  // THD — engineering tightness
  const thd = num(specs["THD"]);
  if (thd > 0 && thd < 0.05) {
    pushFactor(factors, 3, "Low THD (<0.05%)", "Tight output stage and clean PSU — typical of premium engineering.");
    score += 3;
  } else if (thd >= 0.5) {
    pushFactor(factors, -3, `High THD (${thd}%)`, "Above 0.5% THD indicates relaxed engineering tolerances.");
    score -= 3;
  }

  // Fuse-vs-RMS coherence (music program at 40% duty)
  const fuse = totalFuseAmps(specs["Fuse Rating"]);
  const rms = totalAmpRms(specs);
  if (fuse > 0 && rms > 0) {
    const musicAmps = (rms / (assumedEff / 100) / 13.8) * 0.40;
    const ratio = fuse / musicAmps;
    if (ratio < 0.55) {
      pushFactor(factors, -7, "Severely under-fused for rated power",
        `Fuse (${fuse}A) far below the ~${Math.round(musicAmps)}A this amp would pull on real music — RMS rating is almost certainly inflated.`);
      score -= 7;
    } else if (ratio >= 0.85 && ratio <= 1.6) {
      pushFactor(factors, 5, "Fuse matches real-world current draw",
        "Fuse rating is sized correctly for the amp's actual music-program current — honest power claim.");
      score += 5;
    } else if (ratio > 1.6 && ratio < 2.5) {
      pushFactor(factors, 3, "Generous fusing", "Fuse oversized vs music-program draw — headroom for transients.");
      score += 3;
    }
  }

  // Impedance doubling pattern — a healthy Class-D should roughly double from 4->2->1 ohm.
  const ladder = ampImpedanceLadder(specs);
  if (ladder.length >= 2) {
    const high = ladder[ladder.length - 1];
    const low = ladder[0];
    if (high.ohm > 0 && low.ohm > high.ohm) {
      const impedanceRatio = low.ohm / high.ohm;
      const wattsRatio = high.watts / low.watts;
      const coherence = wattsRatio / impedanceRatio;
      if (coherence >= 0.85 && coherence <= 1.15) {
        pushFactor(factors, 6, "Textbook impedance scaling",
          `${low.watts}W @ ${low.ohm}ohm -> ${high.watts}W @ ${high.ohm}ohm (${(coherence * 100).toFixed(0)}% of ideal doubling) — power supply genuinely scales with load.`);
        score += 6;
      } else if (coherence >= 0.7) {
        pushFactor(factors, 3, "Good impedance scaling",
          `Output scales ${(coherence * 100).toFixed(0)}% of ideal — solid PSU for the rated power.`);
        score += 3;
      } else if (coherence < 0.55) {
        pushFactor(factors, -5, "PSU starves at low impedance",
          `Output only scales ${(coherence * 100).toFixed(0)}% of ideal from ${low.ohm}ohm->${high.ohm}ohm — undersized PSU rails will sag and clip early.`);
        score -= 5;
      }
    }
  }

  // Watts-per-pound coherence — topology-aware. Class-D + SMPS legit
  // achieves 300–500 W/lb; A/B linear tops out ~120. Compare within class.
  const weight = num(specs["Weight"]);
  const wppTop = wppPlausibility(rms, weight, ampTopologyClass(specs), fuse);
  if (wppTop) {
    pushFactor(factors, wppTop.delta, wppTop.label, wppTop.detail);
    score += wppTop.delta;
  }

  // Bench-test overlay — independent measurement outranks manufacturer spec.
  const bench = benchTestFactor(p);
  if (bench) {
    pushFactor(factors, bench.delta, bench.label, bench.detail);
    score += bench.delta;
  }

  // Positive-silence signal — flagship/mainstream install base with low
  // complaint volume counter-weights forum's failure-report base-rate bias.
  const silence = positiveSilenceBonus(p);
  if (silence) {
    pushFactor(factors, silence.delta, silence.label, silence.detail);
    score += silence.delta;
  }

  // Low-impedance engineering — having a 1ohm or lower rating means designed for the load
  if (ladder.length > 0 && ladder[ladder.length - 1].ohm <= 1) {
    pushFactor(factors, 3, "Rated stable at 1ohm",
      "Designing for 1ohm requires beefier output FETs and PSU — these survive low-Z subwoofer loads.");
    score += 3;
  }

  // Spec sheet completeness — transparency correlates with QC
  const completenessKeys = ["Weight", "Efficiency", "Fuse Rating", "THD", "Frequency Response", "Input Sensitivity"];
  const present = completenessKeys.filter((k) => specs[k] !== undefined && String(specs[k]).trim() !== "").length;
  if (present >= 5) {
    pushFactor(factors, 4, "Complete spec sheet", "All key specs disclosed — manufacturer not hiding weak numbers.");
    score += 4;
  } else if (present <= 1) {
    pushFactor(factors, -3, "Sparse spec sheet", "Missing core specs is a common cover for inflated claims.");
    score -= 3;
  }

  // Budget x budget brand penalty (narrower window so mid-tier value amps aren't punished)
  if (p.price < 50 && brand < 55) {
    pushFactor(factors, -8, "Budget price + budget brand",
      "Sub-$50 amps from Tier C/D brands have the highest documented DOA + first-year failure rates.");
    score -= 8;
  }

  return finalize(score, baseline, factors, reliabilitySummary);
}

function ampAbuseTolerance(p: Product): ComputedRating {
  const factors: ScoreFactor[] = [];
  const baseline = 55;
  let score = baseline;
  const specs = p.specs as Record<string, string | number | undefined>;

  const power = totalAmpRms(specs);
  const fuse = totalFuseAmps(specs["Fuse Rating"]);
  const eff = num(specs["Efficiency"]);
  const weight = num(specs["Weight"]);
  const channels = num(specs["Channels"]) || 1;
  const ladder = ampImpedanceLadder(specs);

  // Raw power headroom — the further from thermal limit, the more abuse it takes
  if (power > 0) {
    const pPts = smoothUp(power, 1200, 32);
    pushFactor(factors, pPts, `${power.toLocaleString()}W output capability`,
      channels > 1
        ? `Total across ${channels} channels at lowest rated load — headroom from thermal limit.`
        : "Larger amps run further from their thermal limit at any given output level.");
    score += pPts;
  }

  // Fuse capacity = current the chassis is rated to survive
  if (fuse > 0) {
    const fPts = smoothUp(fuse, 120, 12);
    pushFactor(factors, fPts, `${fuse}A total fusing`, "More fuse capacity = headroom for current spikes on bass transients.");
    score += fPts;
  }

  // Watts-per-pound — topology-aware. Only penalize when W/lb is
  // inconsistent with the amp's own class + fuse rating, not universally.
  const topClass = ampTopologyClass(specs);
  const wppTop = wppPlausibility(power, weight, topClass, fuse);
  if (wppTop) {
    // Under-abuse, "generous" gets extra credit because thermal mass is what survives sustained burps.
    const abuseDelta = wppTop.verdict === "generous" ? wppTop.delta + 2
      : wppTop.verdict === "improbable" ? wppTop.delta - 1
      : wppTop.delta;
    pushFactor(factors, abuseDelta, wppTop.label, wppTop.detail);
    score += abuseDelta;
  }

  // Bench-test sustained-load result — the gold standard for abuse-tolerance.
  const bench = benchTestFactor(p);
  if (bench) {
    pushFactor(factors, bench.delta, bench.label, bench.detail);
    score += bench.delta;
  }

  // BTU dissipation budget
  if (power > 0 && eff > 0 && weight > 0) {
    const heatWatts = (1 - eff / 100) * power * 0.5;
    const heatPerLb = heatWatts / weight;
    if (heatPerLb < 25) {
      pushFactor(factors, 4, "Low thermal load per pound of heatsink",
        `Generates only ~${Math.round(heatWatts)}W of waste heat across ${weight} lbs — sustained abuse stays in spec.`);
      score += 4;
    }
  }

  // Low-impedance stability — survives 1ohm and below
  if (ladder.length > 0) {
    const lowestZ = ladder[ladder.length - 1].ohm;
    if (lowestZ <= 0.5) {
      pushFactor(factors, 7, `Stable at ${lowestZ}ohm`,
        "Sub-1ohm rating means doubled FET arrays and beefy traces — survives daily-driver low-Z abuse.");
      score += 7;
    } else if (lowestZ <= 1) {
      pushFactor(factors, 5, "Stable at 1ohm",
        "1ohm rating shows engineered for the load most SPL/daily-driver setups actually run.");
      score += 5;
    } else if (lowestZ <= 2) {
      pushFactor(factors, 2, "Stable at 2ohm",
        "2ohm-stable monoblock / multichannel — covers most factory & aftermarket loads.");
      score += 2;
    }
  }

  // Bridged-rating presence — tested under cross-channel stress
  const hasBridged = Object.keys(specs).some((k) => /^bridged/i.test(k));
  if (hasBridged) {
    pushFactor(factors, 3, "Manufacturer-rated bridged",
      "Published bridged spec = tested under cross-channel stress; safer to push hard.");
    score += 3;
  }

  // High-efficiency power stage
  if (eff >= 88) {
    pushFactor(factors, 4, "High-efficiency power stage", "Less waste heat = more sustained-output headroom.");
    score += 4;
  }

  // Active cooling / fan-equipped
  const cooling = (specs["Cooling"] ?? "").toString().toLowerCase();
  if (cooling.includes("fan") || cooling.includes("active")) {
    pushFactor(factors, 3, "Active forced-air cooling",
      "Onboard fan keeps FET junction temps in spec under sustained competition use.");
    score += 3;
  }

  // Brand multiplier
  const brand = brandRep(p.brand);
  const brandDelta = (brand - 50) * 0.22;
  pushFactor(factors, brandDelta, `${p.brand} engineering margin (${brandTierLabel(brand)})`,
    "Top-tier amp brands measure above their rated specs; budget brands often fall short.");
  score += brandDelta;

  return finalize(score, baseline, factors, abuseSummary);
}

function ampBuildQuality(p: Product): ComputedRating {
  const factors: ScoreFactor[] = [];
  const baseline = 58;
  let score = baseline;
  const specs = p.specs as Record<string, string | number | undefined>;

  // Brand build reputation — single biggest build-quality predictor
  const brand = brandRep(p.brand);
  const brandDelta = (brand - 50) * 0.55;
  pushFactor(factors, brandDelta, `Brand build reputation: ${p.brand} (${brandTierLabel(brand)})`,
    "Reflects PCB grade, FET binning, terminal quality, and unit-to-unit QC variance.");
  score += brandDelta;

  // Heatsink construction
  const heatsink = (specs["Heatsink"] ?? specs["Chassis"] ?? "").toString().toLowerCase();
  if (heatsink.includes("billet") || heatsink.includes("cnc")) {
    pushFactor(factors, 7, "Billet/CNC aluminum heatsink", "CNC-billet body = maximum thermal mass and perfect flatness for FET contact.");
    score += 7;
  } else if (heatsink.includes("extruded")) {
    pushFactor(factors, 5, "Extruded aluminum heatsink", "Extruded body has higher thermal mass than cast — keeps FET temps in spec.");
    score += 5;
  } else if (heatsink.includes("cast")) {
    pushFactor(factors, 2, "Cast aluminum heatsink", "Cast chassis is the industry baseline — adequate for rated power.");
    score += 2;
  }

  // Watts-per-pound — topology-aware reality check. Modern Class-D +
  // SMPS legitimately hits 300+ W/lb; only flag when W/lb is
  // implausible for the amp's own topology class + fuse.
  const power = totalAmpRms(specs);
  const weight = num(specs["Weight"]);
  const fuse = totalFuseAmps(specs["Fuse Rating"]);
  const wppTop = wppPlausibility(power, weight, ampTopologyClass(specs), fuse);
  if (wppTop) {
    // Build-quality skews slightly toward "generous mass = better build".
    const buildDelta = wppTop.verdict === "generous" ? wppTop.delta + 3
      : wppTop.verdict === "improbable" ? wppTop.delta - 2
      : wppTop.delta;
    pushFactor(factors, buildDelta, wppTop.label, wppTop.detail);
    score += buildDelta;
  }

  // Bench-test overlay
  const bench = benchTestFactor(p);
  if (bench) {
    pushFactor(factors, Math.round(bench.delta * 0.6), bench.label, bench.detail);
    score += Math.round(bench.delta * 0.6);
  }

  // Made in
  const origin = (specs["Made In"] ?? specs["Origin"] ?? "").toString().toLowerCase();
  if (/(usa|italy|germany|japan|denmark)/.test(origin)) {
    pushFactor(factors, 6, `Built in ${specs["Made In"] ?? specs["Origin"]}`,
      "Tighter QC tolerances than typical overseas mass production.");
    score += 6;
  } else if (/brazil/.test(origin)) {
    pushFactor(factors, 4, "Built in Brazil",
      "Brazilian amp manufacturing (Taramps, Stetsom, Soundigital) has matured into excellent tolerances.");
    score += 4;
  }

  // THD as fit-and-finish signal
  const thd = num(specs["THD"]);
  if (thd > 0 && thd < 0.05) {
    pushFactor(factors, 3, "Tight THD spec", "Reflects matched output devices and clean PSU layout.");
    score += 3;
  }

  // CEA-2006 / dyno-tested as build-QC signal
  const certs = JSON.stringify(specs).toLowerCase();
  if (/cea[-\s]?2006|birth sheet|dyno tested|individually tested/.test(certs)) {
    pushFactor(factors, 4, "Verified output (CEA-2006 / dyno)",
      "Independent or per-unit verification = build line consistent enough to certify.");
    score += 4;
  }

  // Spec completeness (transparency)
  const completenessKeys = ["Weight", "Efficiency", "Fuse Rating", "THD", "Frequency Response", "Input Sensitivity"];
  const present = completenessKeys.filter((k) => specs[k] !== undefined && String(specs[k]).trim() !== "").length;
  if (present >= 5) {
    pushFactor(factors, 3, "Full spec disclosure", "Complete published specs = nothing being hidden.");
    score += 3;
  }

  // Ultra-budget penalty (narrower trigger so mid-priced amps aren't punished)
  if (p.price < 50 && brand < 55) {
    pushFactor(factors, -8, "Ultra-budget pricing", "Sub-$50 amp builds cut corners on FETs, PSU iron, and chassis machining.");
    score -= 8;
  }

  return finalize(score, baseline, factors, buildSummary);
}

// ═════════════════════════════════════════════════════════════════
//                       ALTERNATOR ALGORITHM
// ─────────────────────────────────────────────────────────────────
// Failure modes that matter for alternators:
//   • Bearing wear → bearing type, idle-RPM operation, weight (oversized rotor)
//   • Diode bridge failure → phase count, rectifier grade, voltage spikes
//   • Stator burnout → continuous vs peak, copper mass (amps/lb), cooling
//   • Regulator failure → internal vs external, voltage spec
//   • Pulley/belt wear → idle ratio (less time at max RPM)
// ═════════════════════════════════════════════════════════════════

function altReliability(p: Product): ComputedRating {
  const factors: ScoreFactor[] = [];
  const baseline = 50;
  let score = baseline;
  const specs = p.specs as Record<string, string | number | undefined>;

  // Brand
  const brand = brandRep(p.brand);
  const brandDelta = (brand - 50) * 0.4;
  pushFactor(factors, brandDelta, `Brand track record: ${p.brand} (${brandTierLabel(brand)})`,
    "Long-term alternator failure rates from Mechman / Singer / DC Power / Ohio Generator owner data.");
  score += brandDelta;

  // Warranty
  const wYears = num(specs["Warranty"]);
  if (wYears > 0) {
    const wPts = smoothUp(wYears, 1.5, 10);
    pushFactor(factors, wPts, `${wYears}-year warranty`,
      "High-output alts typically ship with 1y; lifetime/3y+ signals confidence in bearings + diodes.");
    score += wPts;
  }

  // Idle delivery — high idle = less time at max RPM = less bearing wear
  const idle = num(specs["Output @ Idle"] ?? specs["Idle Output"]);
  const max = num(specs["Max Output"]);
  if (idle > 0 && max > 0) {
    const idleRatio = idle / max;
    // Idle-RPM output is the single most important high-output alt signal:
    // amps draw hardest at low RPM. Weight it heavily.
    if (idleRatio >= 0.60) {
      pushFactor(factors, 11, `Exceptional idle output (${Math.round(idleRatio * 100)}% of max)`,
        "Class-leading hairpin stator + premium rectifier — the alt never has to peg near redline, so bearing/diode life is 3–5× longer.");
      score += 11;
    } else if (idleRatio >= 0.50) {
      pushFactor(factors, 8, `Strong idle output (${Math.round(idleRatio * 100)}% of max)`,
        "Hairpin stator + premium diodes deliver high idle current — bearings & diodes stay well below thermal limits.");
      score += 8;
    } else if (idleRatio >= 0.4) {
      pushFactor(factors, 3, `Healthy idle output (${Math.round(idleRatio * 100)}% of max)`,
        "Above-average idle delivery reduces sustained high-RPM operation.");
      score += 3;
    } else if (idleRatio < 0.3) {
      pushFactor(factors, -6, "Weak idle output",
        "Low idle output forces the alt near max RPM under audio load — the fastest path to bearing/diode failure.");
      score -= 6;
    }
  }

  // Brush vs brushless — brushless eliminates a wear item entirely
  const design = (specs["Design"] ?? specs["Rotor"] ?? specs["Construction"] ?? "").toString().toLowerCase();
  if (design.includes("brushless")) {
    pushFactor(factors, 5, "Brushless design",
      "No brushes or slip rings to wear — eliminates a common 60–100k-mile failure item and reduces sustained-load electrical noise.");
    score += 5;
  }

  // Duty-cycle rating (competition / continuous-duty spec)
  const duty = (specs["Duty Cycle"] ?? specs["Rated Duty"] ?? "").toString().toLowerCase();
  if (/continuous|100%|competition/.test(duty)) {
    pushFactor(factors, 4, "Continuous-duty rated",
      "Rated for 100% sustained output — stator + rectifier are sized for burst-free continuous current, not just peak.");
    score += 4;
  }

  // Bearings — #1 failure mode of high-output alts
  const bearings = (specs["Bearing Type"] ?? specs["Bearings"] ?? "").toString().toLowerCase();
  if (bearings.includes("dual") && bearings.includes("seal")) {
    pushFactor(factors, 7, "Dual sealed bearings",
      "Dual sealed bearings + grease retention double the MTBF over single open bearings — eliminates the #1 alt failure mode.");
    score += 7;
  } else if (bearings.includes("seal")) {
    pushFactor(factors, 4, "Sealed bearings", "Resists road contamination; extends bearing life significantly.");
    score += 4;
  } else if (bearings.includes("open") || bearings.includes("standard")) {
    pushFactor(factors, -3, "Open/standard bearings",
      "Open bearings on a high-output alt collect dust + lose grease — common failure within 2 years of hard use.");
    score -= 3;
  }

  // Phase count — fewer ripples = less diode + stator stress
  const phases = num(specs["Phases"]);
  if (phases >= 8) {
    pushFactor(factors, 5, `${phases}-phase stator`,
      "8+ phase stators run smoothest — almost zero ripple, dramatically lower diode/stator stress.");
    score += 5;
  } else if (phases >= 6) {
    pushFactor(factors, 4, "6-phase stator", "Six-phase stators cut ripple ~50% vs 3-phase — significantly less diode stress.");
    score += 4;
  } else if (phases > 0 && phases < 6) {
    pushFactor(factors, -1, `${phases}-phase stator`, "Lower phase count = higher ripple = more thermal cycling on diodes.");
    score -= 1;
  }

  // Regulator — external/adjustable is huge for lithium safety
  const reg = (specs["Regulator Type"] ?? specs["Regulator"] ?? "").toString().toLowerCase();
  if (reg.includes("external") || reg.includes("adjustable")) {
    pushFactor(factors, 5, "External/adjustable regulator",
      "Lets you dial in safe charge voltage for lithium banks — eliminates a top failure mode (overcharge → regulator melt).");
    score += 5;
  }

  // Voltage spec — 14.4V is the daily-driver sweet spot
  const voltage = num(specs["Voltage"]);
  if (voltage >= 14.2 && voltage <= 14.6) {
    pushFactor(factors, 2, `${voltage}V regulation`, "Sweet-spot regulation voltage — safe for AGM, lithium, and stock electronics.");
    score += 2;
  } else if (voltage > 14.8) {
    pushFactor(factors, -2, `${voltage}V regulation`,
      "Above 14.8V is lithium-only territory — will overcharge AGM/flooded batteries and accelerate regulator wear if mismatched.");
    score -= 2;
  }

  // Amps-per-pound coherence — over-built alts have more copper
  const weight = num(specs["Weight"]);
  if (max > 0 && weight > 0) {
    const app = max / weight;
    if (app > 28) {
      pushFactor(factors, -4, "High A/lb ratio",
        `${max}A in ${weight} lbs = thin copper / lightweight rotor — sustained high-amp draw will overheat the stator.`);
      score -= 4;
    } else if (app < 15) {
      pushFactor(factors, 3, "Low A/lb (oversized for rating)",
        `${weight} lbs for ${max}A = thick copper and oversized rotor — runs cool and lasts.`);
      score += 3;
    }
  }

  return finalize(score, baseline, factors, reliabilitySummary);
}

function altAbuseTolerance(p: Product): ComputedRating {
  const factors: ScoreFactor[] = [];
  const baseline = 45;
  let score = baseline;
  const specs = p.specs as Record<string, string | number | undefined>;

  const max = num(specs["Max Output"]);
  const idle = num(specs["Output @ Idle"] ?? specs["Idle Output"]);
  const weight = num(specs["Weight"]);
  const construction = (specs["Construction"] ?? "").toString().toLowerCase();
  const phases = num(specs["Phases"]);

  // Raw max output — current headroom for SPL bursts
  if (max > 0) {
    const aPts = smoothUp(max, 220, 30);
    pushFactor(factors, aPts, `${max}A max output`,
      "Heavier stator + rectifier diode array handle sustained high-current loads without throttling.");
    score += aPts;
  }

  // Idle output as abuse signal — high idle means it can survive SPL burps at a light
  if (idle > 0 && max > 0) {
    const idleRatio = idle / max;
    if (idleRatio >= 0.55) {
      pushFactor(factors, 6, "High idle output",
        "Strong idle delivery means the alt isn't pegged at redline under SPL bursts — survives sustained abuse far longer.");
      score += 6;
    } else if (idleRatio >= 0.45) {
      pushFactor(factors, 3, "Above-average idle output", "Reasonable headroom even at idle-RPM.");
      score += 3;
    }
  }

  // Construction signals — billet + hairpin = built for abuse
  if (construction.includes("billet")) {
    pushFactor(factors, 7, "Billet construction",
      "CNC-billet case dissipates heat 30%+ better than cast aluminum under continuous high-amp loads.");
    score += 7;
  }
  if (construction.includes("hairpin")) {
    pushFactor(factors, 5, "Hairpin stator winding",
      "Hairpin windings pack ~30% more copper than conventional round-wire — runs cooler and handles surges better.");
    score += 5;
  }
  if (construction.includes("custom wound") || construction.includes("hand wound")) {
    pushFactor(factors, 4, "Custom-wound stator",
      "Hand-wound to target — no factory compromise; copper sized for the rated current.");
    score += 4;
  }

  // Amps-per-pound — overbuilt = more thermal mass to absorb abuse
  if (max > 0 && weight > 0) {
    const app = max / weight;
    if (app < 16) {
      pushFactor(factors, 5, "Heavy-build alternator",
        `${weight} lbs for ${max}A = oversized rotor/stator with serious thermal mass.`);
      score += 5;
    }
  }

  // Phases as abuse signal — smoother current = less peak stress on diodes
  if (phases >= 8) {
    pushFactor(factors, 4, `${phases}-phase rectifier`, "Multi-phase rectifier handles voltage spikes from large cap banks better.");
    score += 4;
  } else if (phases >= 6) {
    pushFactor(factors, 2, "6-phase rectifier", "Lower ripple = lower peak diode stress under load swings.");
    score += 2;
  }

  // Brand multiplier
  const brand = brandRep(p.brand);
  const brandDelta = (brand - 50) * 0.18;
  pushFactor(factors, brandDelta, `${p.brand} engineering margin (${brandTierLabel(brand)})`,
    "High-output alt brands consistently measure above their rated output; budget rebuilds often fall short.");
  score += brandDelta;

  return finalize(score, baseline, factors, abuseSummary);
}

function altBuildQuality(p: Product): ComputedRating {
  const factors: ScoreFactor[] = [];
  const baseline = 50;
  let score = baseline;
  const specs = p.specs as Record<string, string | number | undefined>;

  // Brand build reputation — alternator build is dominated by who wound it
  const brand = brandRep(p.brand);
  const brandDelta = (brand - 50) * 0.45;
  pushFactor(factors, brandDelta, `Brand build reputation: ${p.brand} (${brandTierLabel(brand)})`,
    "Reflects case machining tolerance, winding consistency, and diode binning across the brand's lineup.");
  score += brandDelta;

  // Construction
  const construction = (specs["Construction"] ?? "").toString().toLowerCase();
  if (construction.includes("billet")) {
    pushFactor(factors, 9, "Billet aluminum case",
      "CNC-machined housing = perfect bearing alignment, premium QC, and 30%+ better heat dissipation than cast.");
    score += 9;
  } else if (construction.includes("cast")) {
    pushFactor(factors, 3, "Cast aluminum case", "Adequate but lower thermal performance and looser tolerances than billet.");
    score += 3;
  }
  if (construction.includes("hairpin")) {
    pushFactor(factors, 7, "Hairpin stator winding",
      "Hairpin windings have ~30% more copper fill than conventional — runs cooler, lasts longer.");
    score += 7;
  }
  if (construction.includes("custom wound") || construction.includes("hand wound")) {
    pushFactor(factors, 5, "Custom-wound stator", "Hand-wound for target output — no factory-spec compromise.");
    score += 5;
  }

  // Bearings as build-quality signal
  const bearings = (specs["Bearing Type"] ?? "").toString().toLowerCase();
  if (bearings.includes("dual") && bearings.includes("seal")) {
    pushFactor(factors, 5, "Dual sealed bearings",
      "Premium spec — uses two SKF/NTN-grade sealed bearings; the #1 alt build-quality differentiator.");
    score += 5;
  } else if (bearings.includes("seal")) {
    pushFactor(factors, 3, "Sealed bearings", "Above-standard bearing spec.");
    score += 3;
  }

  // Phases — build-quality signal
  const phases = num(specs["Phases"]);
  if (phases >= 8) {
    pushFactor(factors, 4, `${phases}-phase rectifier`, "Premium spec — requires more diodes and wider rectifier PCB.");
    score += 4;
  } else if (phases >= 6) {
    pushFactor(factors, 3, "6-phase rectifier", "Above-average spec — meaningfully smoother DC output.");
    score += 3;
  }

  // Diodes / rectifier grade
  const diode = (specs["Diode"] ?? specs["Rectifier"] ?? "").toString().toLowerCase();
  if (diode.includes("avalanche") || diode.includes("press-fit") || diode.includes("heavy")) {
    pushFactor(factors, 5, "Heavy-duty rectifier",
      "Upgraded diodes survive the voltage spikes from large cap banks — eliminates a common DOA cause.");
    score += 5;
  }

  // Warranty as QC confidence proxy
  const wYears = num(specs["Warranty"]);
  if (wYears >= 3) {
    pushFactor(factors, 4, `${wYears}-year warranty`,
      "Multi-year warranty on a high-output alt = the manufacturer trusts their own bearings and diodes.");
    score += 4;
  }

  // Origin
  const origin = (specs["Made In"] ?? specs["Origin"] ?? "").toString().toLowerCase();
  if (/(usa)/.test(origin)) {
    pushFactor(factors, 5, "Built in USA",
      "All top high-output alt brands wind and machine in-house in the US — tight tolerances, consistent QC.");
    score += 5;
  }

  // Amps-per-pound build solidity
  const max = num(specs["Max Output"]);
  const weight = num(specs["Weight"]);
  if (max > 0 && weight > 0) {
    const app = max / weight;
    if (app < 14) {
      pushFactor(factors, 4, "Substantial build mass",
        `${weight} lbs for ${max}A = thick copper, oversized rotor — no shortcuts in materials.`);
      score += 4;
    } else if (app > 30) {
      pushFactor(factors, -5, "Underweight for rated output",
        `${weight} lbs cannot physically contain enough copper to sustain ${max}A — likely peak-only spec.`);
      score -= 5;
    }
  }

  // Voltage spec presence + plausibility
  const voltage = num(specs["Voltage"]);
  if (voltage >= 14.2 && voltage <= 14.8) {
    pushFactor(factors, 2, `${voltage}V regulation`, "Published, sensible regulation point — typical of in-house engineered units.");
    score += 2;
  }

  // Ultra-budget penalty (rebuilds from rock-bottom shops)
  if (p.price < 150) {
    pushFactor(factors, -10, "Ultra-budget pricing",
      "Sub-$150 high-output alts are typically reman cores with replaced diodes only — no premium internals.");
    score -= 10;
  }

  return finalize(score, baseline, factors, buildSummary);
}

// ═════════════════════════════════════════════════════════════════
//                        SUBWOOFER ALGORITHM
// ─────────────────────────────────────────────────────────────────
// Failure modes that matter for subs:
//   • Voice-coil burnout (thermal density, cooling features)
//   • Mechanical bottoming (Xmax vs sustained excursion)
//   • Suspension fatigue (spider tear, surround rot, lead-wire fatigue)
//   • Motor sag / demagnetization under sustained abuse
//   • Inflated peak/RMS marketing
//   • Basket flex / former buckling
// ═════════════════════════════════════════════════════════════════

function subSpiderCount(specs: Record<string, string | number | undefined>): number {
  const s = (specs["Spider"] ?? specs["Suspension"] ?? "").toString().toLowerCase();
  if (/triple|3\b/.test(s)) return 3;
  if (/dual|2\b/.test(s)) return 2;
  return 1;
}
function subCoolingScore(specs: Record<string, string | number | undefined>): { pts: number; label: string } | null {
  const blob = [specs["Cooling"], specs["Pole Piece"], specs["Voice Coil Former"], specs["Former"], specs["Motor"], specs["Vent"]]
    .filter(Boolean).join(" ").toLowerCase();
  let pts = 0; const tags: string[] = [];
  if (/vented pole|pole vent/.test(blob)) { pts += 3; tags.push("vented pole"); }
  if (/aluminum former|alum former|alu former/.test(blob)) { pts += 3; tags.push("aluminum former"); }
  if (/flat[- ]wound|edge[- ]wound|ribbon/.test(blob)) { pts += 2; tags.push("flat-wound coil"); }
  if (/forced air|fan|cooled basket|basket vent/.test(blob)) { pts += 2; tags.push("vented basket"); }
  return tags.length ? { pts, label: tags.join(", ") } : null;
}

// ─── Forum / community consensus per category ──────────────────────
// Parses the per-category evidence string (curated from DIYMA, SMD Forum,
// r/CarAV, CarAudio.com long-term threads) for sentiment specific to the
// category being scored. Returns a delta in roughly -12..+12 plus a
// human-readable label so it surfaces in the "Why this rating?" UI.
function subForumConsensus(
  text: string | undefined,
  category: "reliability" | "abuseTolerance" | "buildQuality" | "userReviews",
): { delta: number; label: string; detail: string } | null {
  if (!text) return null;
  const t = text.toLowerCase();

  const genericPos = [
    "highly regarded", "well regarded", "well-regarded", "consensus is",
    "forum favorite", "forum darling", "community favorite", "praised",
    "loved by", "go-to", "go to choice", "respected", "proven", "legendary",
    "bulletproof", "no complaints", "no reported failures", "trusted",
  ];
  const genericNeg = [
    "fanboy hype", "marketing hype", "overhyped", "overrated", "rebadged",
    "rebadge", "inflated specs", "specs are inflated", "questionable",
    "polarizing", "hit or miss", "mixed reviews", "controversial",
    "avoided by", "disliked by", "shop avoids", "comeback queen",
  ];

  const catPos: Record<typeof category, string[]> = {
    reliability: [
      "lasts for years", "years of use", "decades of use", "never failed",
      "still going strong", "rarely fails", "low failure rate",
      "consistently reliable", "long-term reliable", "long term reliable",
      "very dependable",
    ],
    abuseTolerance: [
      "takes a beating", "takes abuse", "takes punishment", "abuse tolerant",
      "handles clipping", "handles clipped", "amp tolerant", "amp-tolerant",
      "survives clipping", "soaks power", "eats power", "loves power",
      "thermal monster", "thermal headroom", "battle tested", "battle-tested",
      "competition proven", "spl proven",
    ],
    buildQuality: [
      "premium build", "premium construction", "built like a tank",
      "tank-like", "overbuilt", "machined", "tight tolerances",
      "qc is excellent", "fit and finish", "beautiful build",
      "heirloom quality",
    ],
    userReviews: [
      "owner consensus", "owners love", "owners praise", "high satisfaction",
      "recommended by owners", "repeat buyers",
    ],
  };
  const catNeg: Record<typeof category, string[]> = {
    reliability: [
      "coil burn", "voice coil failure", "vc burnout", "burns out",
      "spider tear", "spider rip", "surround tear", "surround rot",
      "lead-wire", "lead wire", "tinsel break", "former collapse",
      "former buckle", "demagnetiz", "high doa", "dead on arrival",
      "warranty queen", "common failure", "frequent failures",
      "recurring failure",
    ],
    abuseTolerance: [
      "can't handle", "cannot handle", "fries easily", "fries quickly",
      "delicate", "fragile under", "bottoms out", "unloads",
      "thermally limited", "soft on power",
    ],
    buildQuality: [
      "flimsy", "cheap feeling", "cheap build", "basket flex", "poor qc",
      "qc issues", "inconsistent build", "rattle out of the box",
      "loose tolerances", "thin top plate",
    ],
    userReviews: [
      "polarized", "polarised", "fake reviews", "review bombed",
      "review-bombed", "shill", "astroturf", "complaints pile up",
    ],
  };

  const pos =
    genericPos.filter((k) => t.includes(k)).length +
    catPos[category].filter((k) => t.includes(k)).length;
  const neg =
    genericNeg.filter((k) => t.includes(k)).length +
    catNeg[category].filter((k) => t.includes(k)).length;

  const widespread = /(many users|widespread|commonly reported|frequently reported|recurring|consistent reports|long-term threads|long term threads)/.test(t);
  const isolated = /(rare|isolated|one[- ]off|occasional|edge case|edge-case|few reports)/.test(t);

  if (pos === 0 && neg === 0) return null;

  let delta = pos * 2.2 - neg * 2.8;
  if (widespread) delta *= 1.35;
  if (isolated) delta *= 0.55;
  delta = Math.max(-12, Math.min(12, delta));

  const tone = delta >= 0 ? "positive" : "negative";
  const label =
    delta >= 0
      ? `Forum consensus: ${tone} (${pos} signal${pos === 1 ? "" : "s"})`
      : `Forum consensus: ${tone} (${neg} concern${neg === 1 ? "" : "s"})`;
  const detail = `Aggregated from DIYMA, SMD Forum, r/CarAV, and CarAudio.com long-term threads — what real owners and installers actually say about ${category === "userReviews" ? "owner satisfaction" : category} for this sub.`;
  return { delta: +delta.toFixed(2), label, detail };
}

function subReliability(p: Product): ComputedRating {
  const factors: ScoreFactor[] = [];
  const baseline = 50;
  let score = baseline;
  const specs = p.specs as Record<string, string | number | undefined>;

  const brand = brandRep(p.brand);
  const brandDelta = (brand - 50) * 0.4;
  pushFactor(factors, brandDelta, `Brand track record: ${p.brand} (${brandTierLabel(brand)})`,
    "Long-term sub failure rates tracked across Crutchfield reviews, SMD Forum RMA threads, and r/CarAV teardowns.");
  score += brandDelta;

  const wYears = num(specs["Warranty"]);
  if (wYears > 0) {
    const wPts = smoothUp(wYears, 2.5, 12);
    pushFactor(factors, wPts, `${wYears}-year warranty`,
      "Subs are warranty-heavy products; multi-year coverage signals confidence in coil + suspension.");
    score += wPts;
  } else if (specs["Warranty"] !== undefined) {
    pushFactor(factors, -4, "Limited / 90-day warranty", "Short coverage on a sub almost always reflects budget motor/suspension.");
    score -= 4;
  }

  const vc = num(specs["Voice Coil"]);
  const rms = num(specs["RMS Power"]);
  const peak = num(specs["Peak Power"]);
  const xmax = num(specs["Xmax"]);

  if (vc > 0) {
    const vcPts = smoothUp(vc, 2.5, 12);
    pushFactor(factors, vcPts, `${vc}" voice coil`,
      "Larger coils have more wire surface to dump heat into the motor — the #1 predictor of thermal survival.");
    score += vcPts;
  }

  // Thermal density — RMS per inch of coil circumference proxy
  if (vc > 0 && rms > 0) {
    const coilArea = Math.PI * vc * 1.0;
    const density = rms / coilArea;
    if (density > 220) {
      const penalty = -Math.min(10, (density - 220) / 25);
      pushFactor(factors, penalty, "High thermal density",
        `${rms}W RMS on a ${vc}" coil is aggressive — coil burnout risk rises sharply once amps clip.`);
      score += penalty;
    } else if (density < 90 && rms >= 500) {
      pushFactor(factors, 5, "Conservative thermal density",
        "Plenty of coil surface area for the rated power — runs cool under sustained burps.");
      score += 5;
    }
  }

  const cooling = subCoolingScore(specs);
  if (cooling) {
    pushFactor(factors, cooling.pts, `Active cooling (${cooling.label})`,
      "Vented poles, aluminum formers, and flat-wound coils each measurably extend coil life under hard use.");
    score += cooling.pts;
  }

  if (peak > 0 && rms > 0) {
    const ratio = peak / rms;
    if (ratio > 3.5) {
      pushFactor(factors, -6, "Inflated peak-power claim",
        `Peak ${peak}W vs RMS ${rms}W (${ratio.toFixed(1)}×) is a budget marketing pattern — real handling is closer to RMS.`);
      score -= 6;
    } else if (ratio >= 1.8 && ratio <= 2.4) {
      pushFactor(factors, 4, "Honest peak/RMS ratio",
        "2:1 peak ratio matches CEA-2031 burst measurement — published RMS is realistic.");
      score += 4;
    }
  }

  const spiders = subSpiderCount(specs);
  if (spiders >= 3) {
    pushFactor(factors, 6, "Triple-stacked spider",
      "Three spiders dramatically reduce cone rock and lead-wire fatigue — a major durability upgrade.");
    score += 6;
  } else if (spiders === 2) {
    pushFactor(factors, 4, "Dual spider",
      "Two spiders keep the coil centered under high Xmax — eliminates rub-and-burn failures.");
    score += 4;
  }

  if (xmax >= 15 && spiders < 2) {
    pushFactor(factors, -3, "High Xmax + single spider",
      "Big claimed excursion on a single spider tears suspension early — common on inflated-spec budget subs.");
    score -= 3;
  }

  // Field-failure evidence
  const evidence = (p.evidence?.reliability ?? p.evidence?.abuseTolerance ?? "").toLowerCase();
  if (evidence) {
    const failKeywords = [
      "coil burn", "voice coil failure", "vc burnout", "coil burnout",
      "spider tear", "spider rip", "lead wire", "lead-wire", "tinsel break",
      "surround tear", "surround rot", "surround separation", "cone separation",
      "former collapse", "former buckle", "demagnetiz",
    ];
    const hits = failKeywords.filter((kw) => evidence.includes(kw)).length;
    if (hits >= 1) {
      const penalty = Math.min(hits * 5, 14);
      pushFactor(factors, -penalty, `Field-failure reports (${hits} flag${hits > 1 ? "s" : ""})`,
        "Expert evidence cites real-world failure modes — pulls reliability score down materially.");
      score -= penalty;
    }
  }

  if (p.price < 60 && brand < 65) {
    pushFactor(factors, -8, "Budget price + budget brand",
      "Sub-$60 subs from Tier C/D brands have the highest documented coil/spider failure rates.");
    score -= 8;
  }

  const fcRel = subForumConsensus(p.evidence?.reliability, "reliability");
  if (fcRel) { pushFactor(factors, fcRel.delta, fcRel.label, fcRel.detail); score += fcRel.delta; }

  return finalize(score, baseline, factors, reliabilitySummary);
}

function subAbuseTolerance(p: Product): ComputedRating {
  const factors: ScoreFactor[] = [];
  const baseline = 45;
  let score = baseline;
  const specs = p.specs as Record<string, string | number | undefined>;

  const rms = num(specs["RMS Power"]);
  const peak = num(specs["Peak Power"]);
  const xmax = num(specs["Xmax"]);
  const vc = num(specs["Voice Coil"]);
  const sens = num(specs["Sensitivity"]);
  const motorWeight = num(specs["Magnet Weight"] ?? specs["Motor Weight"]);
  const weight = num(specs["Weight"]);
  const size = num(specs["Size"] ?? specs["Diameter"]);
  const anchor = sizeAnchor(size || 12);

  // Heavily-weighted RMS — the #1 abuse signal, normalized to size class
  // so an 8" sub isn't punished for not being a 15".
  if (rms > 0) {
    const ratio = rms / anchor.rms;
    const rmsPts = Math.min(33, 30 * (1 - Math.exp(-1.6 * ratio)));
    pushFactor(factors, rmsPts, `${rms.toLocaleString()}W RMS handling`,
      `Sustained-power thermal headroom for a ${size || "—"}" driver (anchor: ${Math.round(anchor.rms)}W for class).`);
    score += rmsPts;
  }

  // Mechanical work capacity — also size-normalized via Vd anchor.
  if (rms > 0 && xmax > 0) {
    const sdCm2 = size > 0 ? Math.PI * Math.pow((size * 2.54) / 2, 2) * 0.78 : 510;
    const vdL = (sdCm2 * xmax * 2) / 1000;
    const work = rms * xmax;
    const workRef = anchor.rms * (anchor.vdL * 1000 / sdCm2 / 2);
    const ratio = workRef > 0 ? work / workRef : 0;
    const workPts = Math.min(24, 22 * (1 - Math.exp(-1.6 * ratio)));
    pushFactor(factors, workPts, "Mechanical work capacity",
      `Vd ${vdL.toFixed(2)}L × ${rms}W — proxy for sustained cone energy vs class average.`);
    score += workPts;
  }

  if (vc > 0) {
    const vcPts = smoothUp(vc, 2.5, 14);
    pushFactor(factors, vcPts, `${vc}" voice coil`,
      "Bigger coil mass absorbs more clipped-signal heat before the former buckles or coil shorts.");
    score += vcPts;
  }

  if (xmax > 0) {
    const xPts = smoothUp(xmax, 18, 10);
    pushFactor(factors, xPts, `${xmax}mm Xmax`,
      "Linear excursion headroom — keeps the coil in the gap during burps so it doesn't unload and slap.");
    score += xPts;
  }

  if (peak > 0 && rms > 0) {
    const ratio = peak / rms;
    if (ratio >= 2.0 && ratio <= 3.0) {
      pushFactor(factors, 4, "Healthy burst headroom",
        "2-3× peak/RMS ratio survives bass transients without coil stress.");
      score += 4;
    } else if (ratio > 3.5) {
      pushFactor(factors, -5, "Inflated peak/RMS ratio",
        "Marketing peak >3.5× real RMS — pushing toward 'peak' will fry the coil fast.");
      score -= 5;
    }
  }

  if (sens > 0 && sens < 84 && rms >= 1000) {
    pushFactor(factors, 4, "Low-sensitivity SPL motor",
      "Heavy motor and tight gap trade efficiency for raw mechanical/thermal headroom — built to be beaten on.");
    score += 4;
  }

  const spiders = subSpiderCount(specs);
  if (spiders >= 3) {
    pushFactor(factors, 6, "Triple-stacked spider", "Survives daily-driver excursion without lead-wire fatigue.");
    score += 6;
  } else if (spiders === 2) {
    pushFactor(factors, 4, "Dual spider", "Keeps coil centered under heavy abuse — fewer rub-and-burn failures.");
    score += 4;
  }

  if (motorWeight >= 200) {
    pushFactor(factors, 6, `${motorWeight}oz motor`,
      "Massive motor resists demagnetization and sinks heat from the coil — SPL-grade construction.");
    score += 6;
  } else if (motorWeight >= 100) {
    pushFactor(factors, 3, `${motorWeight}oz motor`,
      "Heavy motor provides solid magnetic field and thermal mass.");
    score += 3;
  } else if (weight >= 35 && rms >= 1000) {
    pushFactor(factors, 3, "Heavy overall mass for rated RMS",
      `${weight} lbs on a ${rms}W sub usually means oversized motor + thick basket — built for abuse.`);
    score += 3;
  }

  const cooling = subCoolingScore(specs);
  if (cooling) {
    pushFactor(factors, cooling.pts + 1, `Active cooling (${cooling.label})`,
      "Vented poles + aluminum formers extend continuous-power survival 30-50% over passively-cooled coils.");
    score += cooling.pts + 1;
  }

  const evidence = (p.evidence?.abuseTolerance ?? "").toLowerCase();
  if (evidence) {
    const failKeywords = [
      "coil burn", "voice coil failure", "vc burnout", "burns out",
      "spider tear", "lead wire", "tinsel", "former collapse", "former buckle",
      "surround tear", "cone separation", "bottoms out",
    ];
    const hits = failKeywords.filter((kw) => evidence.includes(kw)).length;
    if (hits >= 1) {
      const penalty = Math.min(hits * 6, 16);
      pushFactor(factors, -penalty, `Field-failure reports (${hits} flag${hits > 1 ? "s" : ""})`,
        "Expert evidence cites real-world abuse failures — pulls score down materially.");
      score -= penalty;
    }
  }

  const brand = brandRep(p.brand);
  const brandDelta = (brand - 50) * 0.18;
  pushFactor(factors, brandDelta, `${p.brand} engineering margin (${brandTierLabel(brand)})`,
    "Top-tier sub brands consistently measure above rated RMS; budget brands often fall short.");
  score += brandDelta;

  const fcAbuse = subForumConsensus(p.evidence?.abuseTolerance, "abuseTolerance");
  if (fcAbuse) { pushFactor(factors, fcAbuse.delta, fcAbuse.label, fcAbuse.detail); score += fcAbuse.delta; }

  return finalize(score, baseline, factors, abuseSummary);
}

function subBuildQuality(p: Product): ComputedRating {
  const factors: ScoreFactor[] = [];
  const baseline = 50;
  let score = baseline;
  const specs = p.specs as Record<string, string | number | undefined>;

  const brand = brandRep(p.brand);
  const brandDelta = (brand - 50) * 0.45;
  pushFactor(factors, brandDelta, `Brand build reputation: ${p.brand} (${brandTierLabel(brand)})`,
    "Reflects motor magnet grade, basket machining, and unit-to-unit QC variance across the lineup.");
  score += brandDelta;

  const basket = (specs["Basket"] ?? specs["Chassis"] ?? "").toString().toLowerCase();
  if (basket.includes("cast") && basket.includes("aluminum")) {
    pushFactor(factors, 10, "Cast aluminum basket",
      "Rigid + heat-dissipating + dimensionally stable — the gold standard for high-excursion subs.");
    score += 10;
  } else if (basket.includes("cast")) {
    pushFactor(factors, 7, "Cast basket", "Rigid frame — won't ring or flex at high Xmax.");
    score += 7;
  } else if (basket.includes("steel") || basket.includes("stamped")) {
    pushFactor(factors, -2, "Stamped steel basket",
      "Flexes under hard excursion — causes coil misalignment and accelerates suspension wear.");
    score -= 2;
  }

  const cone = (specs["Cone"] ?? "").toString().toLowerCase();
  if (cone.includes("kevlar") || cone.includes("carbon")) {
    pushFactor(factors, 8, "Composite cone (Kevlar/carbon)",
      "Highest stiffness-to-weight — resists cone breakup where paper cones flex and lose output.");
    score += 8;
  } else if (cone.includes("composite") || cone.includes("glass") || cone.includes("fiber")) {
    pushFactor(factors, 5, "Composite/fiber cone", "Stiffer than paper or polymer — better pistonic motion at high output.");
    score += 5;
  } else if (cone.includes("treated paper") || cone.includes("treated pulp")) {
    pushFactor(factors, 3, "Treated paper cone", "Light and well-damped — the SQ standard, vulnerable to moisture.");
    score += 3;
  } else if (cone.includes("polypropylene") || cone.includes("poly")) {
    pushFactor(factors, 1, "Polymer cone", "Weather-resistant but heavier and less stiff than composites.");
    score += 1;
  }

  const surround = (specs["Surround"] ?? "").toString().toLowerCase();
  if (surround.includes("rubber") || surround.includes("nbr")) {
    pushFactor(factors, 5, "Rubber/NBR surround",
      "Lasts 10+ years; resists ozone and UV — the standard for car-audio subs.");
    score += 5;
  } else if (surround.includes("foam")) {
    pushFactor(factors, -4, "Foam surround",
      "Foam rots in 5-7 years from heat and humidity — a known long-term failure path.");
    score -= 4;
  }

  const spider = (specs["Spider"] ?? specs["Suspension"] ?? "").toString().toLowerCase();
  const spiders = subSpiderCount(specs);
  if (spider.includes("nomex") || spider.includes("conex")) {
    pushFactor(factors, 4, "Nomex/Conex spider",
      "High-temp aramid spider — survives heat soak that warps cotton spiders.");
    score += 4;
  }
  if (spiders >= 3) {
    pushFactor(factors, 6, "Triple spider stack", "Premium suspension build — eliminates coil rock under heavy excursion.");
    score += 6;
  } else if (spiders === 2) {
    pushFactor(factors, 4, "Dual spider", "Above-standard suspension — better coil alignment than single-spider builds.");
    score += 4;
  }

  const former = (specs["Voice Coil Former"] ?? specs["Former"] ?? "").toString().toLowerCase();
  if (former.includes("aluminum") || former.includes("alu")) {
    pushFactor(factors, 5, "Aluminum voice-coil former",
      "Wicks heat from the coil into the motor — premium spec, much higher continuous-power survival.");
    score += 5;
  } else if (former.includes("kapton") || former.includes("nomex")) {
    pushFactor(factors, 3, "Aramid (Kapton/Nomex) former",
      "Heat-tolerant former — won't soften under hard use like fiberglass formers.");
    score += 3;
  } else if (former.includes("glass") || former.includes("fiber")) {
    pushFactor(factors, -2, "Fiberglass former", "Heat-soaks easily — common on budget subs.");
    score -= 2;
  }

  const cooling = subCoolingScore(specs);
  if (cooling) {
    pushFactor(factors, cooling.pts, `Cooling features (${cooling.label})`,
      "Vented poles + aluminum formers + flat-wound coils each add real cost; their presence is a build-quality signal.");
    score += cooling.pts;
  }

  const motorWeight = num(specs["Magnet Weight"] ?? specs["Motor Weight"]);
  if (motorWeight >= 200) {
    pushFactor(factors, 6, `${motorWeight}oz motor`,
      "Heavy motor = serious top-plate thickness + magnet stack — SPL-grade construction.");
    score += 6;
  } else if (motorWeight >= 100) {
    pushFactor(factors, 3, `${motorWeight}oz motor`, "Above-average magnet mass — solid motor build.");
    score += 3;
  }

  const terms = (specs["Terminals"] ?? specs["Terminal Type"] ?? "").toString().toLowerCase();
  if (terms.includes("nickel") || terms.includes("spring") || terms.includes("push")) {
    pushFactor(factors, 2, "Premium terminal type", "Spring/nickel terminals signal attention to electrical detail.");
    score += 2;
  }

  const origin = (specs["Made In"] ?? specs["Origin"] ?? "").toString().toLowerCase();
  if (/(usa|italy|germany|japan|denmark)/i.test(origin)) {
    pushFactor(factors, 5, `Built in ${specs["Made In"] ?? specs["Origin"]}`,
      "Tighter QC tolerances than typical overseas mass-production.");
    score += 5;
  }

  if (p.price < 40) {
    pushFactor(factors, -10, "Ultra-budget pricing",
      "Sub-$40 subs cut corners on basket, magnet grade, surround compound, and QC inspection.");
    score -= 10;
  }

  const fcBuild = subForumConsensus(p.evidence?.buildQuality, "buildQuality");
  if (fcBuild) { pushFactor(factors, fcBuild.delta, fcBuild.label, fcBuild.detail); score += fcBuild.delta; }

  return finalize(score, baseline, factors, buildSummary);
}

// ═════════════════════════════════════════════════════════════════
//              TRANSDUCER ALGORITHMS (speaker / tweeter)
// ─────────────────────────────────────────────────────────────────
// Failure modes here are dominated by:
//   • Voice-coil burnout (thermal density)
//   • Suspension fatigue (surround/spider materials)
//   • Inflated peak/RMS marketing
//   • Basket flex / cone breakup
// ═════════════════════════════════════════════════════════════════

function transducerReliability(p: Product): ComputedRating {
  const factors: ScoreFactor[] = [];
  const baseline = 50;
  let score = baseline;

  const brand = brandRep(p.brand);
  const brandDelta = (brand - 50) * 0.4;
  pushFactor(factors, brandDelta, `Brand track record: ${p.brand} (${brandTierLabel(brand)})`,
    "Long-term failure rates and warranty-claim data tracked across Crutchfield, SMD Forum, and r/CarAV.");
  score += brandDelta;

  const wYears = num(p.specs["Warranty"]);
  if (wYears > 0) {
    const wPts = smoothUp(wYears, 2.5, 12);
    pushFactor(factors, wPts, `${wYears}-year warranty`,
      "Manufacturer-funded coverage length is a strong real-world durability signal.");
    score += wPts;
  } else if (p.specs["Warranty"] !== undefined) {
    pushFactor(factors, -4, "Limited / 90-day warranty", "Short coverage typically reflects budget construction.");
    score -= 4;
  }

  const vc = num(p.specs["Voice Coil"]);
  const rms = num(p.specs["RMS Power"]);
  const peak = num(p.specs["Peak Power"]);

  if (vc > 0) {
    const vcPts = smoothUp(vc, 2.5, 11);
    pushFactor(factors, vcPts, `${vc}" voice coil`, "Larger coils dissipate heat better — fewer thermal failures.");
    score += vcPts;
  }
  if (vc > 0 && rms > 0) {
    const coilArea = Math.PI * vc * 1.0;
    const density = rms / coilArea;
    if (density > 200) {
      const penalty = -Math.min(8, (density - 200) / 30);
      pushFactor(factors, penalty, "High thermal density",
        "Rated RMS is high relative to voice-coil cooling area — increases burnout risk.");
      score += penalty;
    } else if (density < 90 && rms >= 200) {
      pushFactor(factors, 4, "Low thermal density",
        "Conservative power-to-coil ratio — runs cool under sustained use.");
      score += 4;
    }
  }
  if (peak > 0 && rms > 0) {
    const ratio = peak / rms;
    if (ratio > 3.5) {
      pushFactor(factors, -5, "Inflated peak-power claim",
        "Peak >3.5× RMS is a marketing pattern common to budget brands; real-world handling is closer to RMS.");
      score -= 5;
    } else if (ratio >= 1.8 && ratio <= 2.4) {
      pushFactor(factors, 3, "Honest peak/RMS ratio",
        "2:1 peak ratio matches industry-standard burst measurement.");
      score += 3;
    }
  }

  if (p.price < 50 && brand < 65) {
    pushFactor(factors, -8, "Budget price + budget brand",
      "Sub-$50 from a Tier C/D brand has the highest documented failure rates.");
    score -= 8;
  }

  return finalize(score, baseline, factors, reliabilitySummary);
}

function transducerAbuseTolerance(p: Product): ComputedRating {
  const factors: ScoreFactor[] = [];
  const baseline = 45;
  let score = baseline;

  if (p.category === "subwoofer") {
    const rms = num(p.specs["RMS Power"]);
    const peak = num(p.specs["Peak Power"]);
    const xmax = num(p.specs["Xmax"]);
    const vc = num(p.specs["Voice Coil"]);
    const sens = num(p.specs["Sensitivity"]);

    const work = rms * xmax;
    const workPts = smoothUp(work, 30000, 28);
    pushFactor(factors, workPts, "Mechanical work capacity",
      `RMS × Xmax = ${Math.round(work).toLocaleString()} — proxy for sustained cone energy without bottoming.`);
    score += workPts;

    if (vc > 0) {
      const vcPts = smoothUp(vc, 2.5, 12);
      pushFactor(factors, vcPts, `${vc}" voice coil`, "Bigger coil = bigger heat sink under prolonged abuse.");
      score += vcPts;
    }
    if (peak > 0 && rms > 0) {
      const ratio = peak / rms;
      if (ratio >= 2.0 && ratio <= 3.0) {
        pushFactor(factors, 4, "Healthy burst headroom", "2-3× peak/RMS ratio survives transient hits cleanly.");
        score += 4;
      }
    }
    if (sens > 0 && sens < 84 && rms >= 1000) {
      pushFactor(factors, 3, "Low-sensitivity SPL motor", "Heavy motor structure trades efficiency for raw output ceiling.");
      score += 3;
    }
  }

  if (p.category === "speaker" || p.category === "tweeter") {
    const rms = num(p.specs["RMS Power"]);
    const peak = num(p.specs["Peak Power"]);
    // ── Real power handling is heavily weighted (the user's #1 abuse-tolerance signal). ──
    if (rms > 0) {
      // Was: smoothUp(rms, 80, 18). Now ~50% steeper curve up to 30 pts.
      const rmsPts = smoothUp(rms, 90, 28);
      pushFactor(factors, rmsPts, `${rms}W RMS power handling`,
        "Real-world thermal headroom — sustained-volume + clipped-signal survival is dominated by this.");
      score += rmsPts;
    }
    // Voice-coil thermal mass proxy via dome size when explicit VC not given.
    const dome = (p.specs["Tweeter Type"] ?? p.specs["Dome"] ?? "").toString();
    const domeMm = num(dome.match(/(\d+(?:\.\d+)?)/)?.[1] ?? 0);
    if (domeMm >= 1.1) {
      pushFactor(factors, 4, `${domeMm}" tweeter dome`, "Larger dome = more thermal mass, more clipping survival.");
      score += 4;
    } else if (domeMm > 0 && domeMm < 0.85) {
      pushFactor(factors, -5, `${domeMm}" small tweeter dome`, "Sub-1\" tweeters fry quickly when amps clip.");
      score -= 5;
    }
    // Peak/RMS ratio sanity: > 3× is a marketing-inflated peak rating, real abuse will surface tweeter failures.
    if (peak > 0 && rms > 0 && peak / rms > 3.2) {
      pushFactor(factors, -4, "Inflated peak/RMS ratio", "Marketing peak >3.2× real RMS often correlates with budget tweeter failures.");
      score -= 4;
    }
    // ── Field-failure penalty: derived from expert evidence string. ──
    const evidence = (p.evidence?.abuseTolerance ?? "").toLowerCase();
    if (evidence) {
      const failKeywords = [
        "voice coil burn", "voice coil failure", "vc burnout", "coil burnout",
        "tweeter failure", "tweeter fries", "tweeter dies", "tweeter fatigue",
        "surround tear", "surround rot", "spider tear", "delicate",
        "clipping kills", "clipping fries", "burns out",
      ];
      const hits = failKeywords.filter((kw) => evidence.includes(kw)).length;
      if (hits >= 1) {
        const penalty = Math.min(hits * 6, 15);
        pushFactor(factors, -penalty, `Field-failure reports (${hits} flag${hits > 1 ? "s" : ""})`,
          "Expert evidence cites real-world abuse failures — pulls score down materially.");
        score -= penalty;
      }
    }
  }


  const brand = brandRep(p.brand);
  const brandDelta = (brand - 50) * 0.18;
  pushFactor(factors, brandDelta, `${p.brand} engineering margin (${brandTierLabel(brand)})`,
    "Top-tier brands measure above their rated specs; budget brands often fall short of theirs.");
  score += brandDelta;

  return finalize(score, baseline, factors, abuseSummary);
}

function transducerBuildQuality(p: Product): ComputedRating {
  const factors: ScoreFactor[] = [];
  const baseline = 50;
  let score = baseline;

  const brand = brandRep(p.brand);
  const brandDelta = (brand - 50) * 0.45;
  pushFactor(factors, brandDelta, `Brand build reputation: ${p.brand} (${brandTierLabel(brand)})`,
    "Reflects materials grade, fit-and-finish, and inter-unit QC variance across the brand's lineup.");
  score += brandDelta;

  const basket = (p.specs["Basket"] ?? p.specs["Chassis"] ?? "").toString().toLowerCase();
  if (basket.includes("cast") && basket.includes("aluminum")) {
    pushFactor(factors, 9, "Cast aluminum basket", "Rigid, dissipates heat, and resists flex under high power.");
    score += 9;
  } else if (basket.includes("cast")) {
    pushFactor(factors, 7, "Cast basket", "Rigid construction — won't ring or flex like stamped steel.");
    score += 7;
  } else if (basket.includes("steel")) {
    pushFactor(factors, 1, "Stamped steel basket", "Adequate but can flex under heavy excursion.");
    score += 1;
  }

  const cone = (p.specs["Cone"] ?? "").toString().toLowerCase();
  if (cone.includes("kevlar") || cone.includes("carbon")) {
    pushFactor(factors, 6, "Composite cone (Kevlar/carbon)", "Highest stiffness-to-weight; resists deformation at extreme SPL.");
    score += 6;
  } else if (cone.includes("composite") || cone.includes("glass")) {
    pushFactor(factors, 4, "Composite cone");
    score += 4;
  } else if (cone.includes("polypropylene") || cone.includes("treated")) {
    pushFactor(factors, 2, "Treated polymer cone", "Weather-resistant; common in marine-rated drivers.");
    score += 2;
  }

  const surround = (p.specs["Surround"] ?? "").toString().toLowerCase();
  if (surround.includes("rubber") || surround.includes("nbr")) {
    pushFactor(factors, 4, "Rubber/NBR surround", "Lasts 10+ years vs foam which rots in 5-7.");
    score += 4;
  } else if (surround.includes("foam")) {
    pushFactor(factors, -3, "Foam surround", "Foam degrades faster from heat and humidity.");
    score -= 3;
  }

  const spider = (p.specs["Spider"] ?? "").toString().toLowerCase();
  if (spider.includes("conex") || spider.includes("nomex") || spider.includes("dual")) {
    pushFactor(factors, 3, "Premium spider material", "High-temperature progressive suspension extends life.");
    score += 3;
  }

  const origin = (p.specs["Made In"] ?? p.specs["Origin"] ?? "").toString().toLowerCase();
  if (/(usa|italy|germany|japan|denmark)/i.test(origin)) {
    pushFactor(factors, 5, `Built in ${p.specs["Made In"] ?? p.specs["Origin"]}`,
      "Tighter QC tolerances than typical overseas mass-production.");
    score += 5;
  }

  if (p.category === "tweeter") {
    // FR-first performance score (bench-preferred). Material is a
    // tie-breaker only, added after the primary measurement-based logic.
    const perf = tweeterPerformanceScore(p);
    // Scale into build-quality contribution (0..12 pts).
    const perfPts = Math.round((perf.score - 50) * 0.24);
    if (Math.abs(perfPts) >= 1) {
      pushFactor(factors, perfPts, "FR flatness + breakup analysis",
        "Primary tweeter-quality signals: response deviation, breakup mode vs crossover, power compression, dispersion.");
      score += perfPts;
    }
    // Material tiebreaker only (0..3 pts). Never dominates FR/breakup.
    const tb = tweeterMaterialTiebreak(p);
    if (tb > 0) {
      pushFactor(factors, tb, "Dome material (tiebreaker)",
        "Secondary signal — only matters when two tweeters score equally on measurable criteria.");
      score += tb;
    }
  }

  if (p.price < 30) {
    pushFactor(factors, -10, "Ultra-budget pricing", "Sub-$30 builds cut corners on materials and QC.");
    score -= 10;
  }

  return finalize(score, baseline, factors, buildSummary);
}

// ─── Shared summary text ──────────────────────────────────────────
function reliabilitySummary(final: number): string {
  return final >= 85 ? "Excellent long-term reliability — engineering and QC both check out."
    : final >= 70 ? "Above-average reliability for typical daily use."
    : final >= 55 ? "Average reliability — fine for moderate use; mind the warranty."
    : "Below-average reliability — best for occasional or budget use only.";
}
function abuseSummary(final: number): string {
  return final >= 85 ? "Built to handle sustained hard abuse and over-power."
    : final >= 70 ? "Tolerates aggressive use within rated power."
    : final >= 55 ? "Fine for normal use — avoid sustained over-power."
    : "Stay at or below rated power; not built for abuse.";
}
function buildSummary(final: number): string {
  return final >= 85 ? "Premium materials and tight tolerances — top-shelf build."
    : final >= 70 ? "Above-average build with quality materials throughout."
    : final >= 55 ? "Standard mid-grade build quality."
    : "Budget construction — expect compromises in materials.";
}
function finalize(score: number, baseline: number, factors: ScoreFactor[], summarize: (n: number) => string): ComputedRating {
  const final = clamp100(score);
  return { score: final, baseline, factors, summary: summarize(final) };
}

// ═════════════════════════════════════════════════════════════════
// Dispatch
// ═════════════════════════════════════════════════════════════════
function computeReliability(p: Product): ComputedRating {
  switch (p.category) {
    case "amplifier":  return ampReliability(p);
    case "alternator": return altReliability(p);
    case "subwoofer":  return subReliability(p);
    default:           return transducerReliability(p);
  }
}
function computeAbuseTolerance(p: Product): ComputedRating {
  switch (p.category) {
    case "amplifier":  return ampAbuseTolerance(p);
    case "alternator": return altAbuseTolerance(p);
    case "subwoofer":  return subAbuseTolerance(p);
    default:           return transducerAbuseTolerance(p);
  }
}
function computeBuildQuality(p: Product): ComputedRating {
  switch (p.category) {
    case "amplifier":  return ampBuildQuality(p);
    case "alternator": return altBuildQuality(p);
    case "subwoofer":  return subBuildQuality(p);
    default:           return transducerBuildQuality(p);
  }
}

// ═════════════════════════════════════════════════════════════════
// Public API
// ═════════════════════════════════════════════════════════════════
export function computeRatings(p: Product): ProductRatings {
  return {
    reliability: computeReliability(p),
    abuseTolerance: computeAbuseTolerance(p),
    buildQuality: computeBuildQuality(p),
  };
}

// ═════════════════════════════════════════════════════════════════
// User-reviews scoring (Bayesian shrinkage + confidence weighting)
// ═════════════════════════════════════════════════════════════════
export interface UserReviewsData {
  score: number;
  averageRating: number;
  reviewCount: number;
  confidence: "low" | "medium" | "high";
  sources: { name: string; url: string; rating?: number; count?: number }[];
  summary?: string;
  lastRefreshed?: string;
}

const CATEGORY_PRIORS: Record<ProductCategory, number> = {
  subwoofer: 4.3,
  speaker: 4.2,
  tweeter: 4.3,
  amplifier: 4.1,
  alternator: 4.2,
};
const PRIOR_WEIGHT = 25;

export function bayesianReviewScore(
  avg: number,
  count: number,
  category: ProductCategory,
  priorOverride?: number,
): number {
  const prior = priorOverride ?? CATEGORY_PRIORS[category] ?? 4.2;
  const safeAvg = clamp01(avg / 5) * 5;
  const posterior = (safeAvg * count + prior * PRIOR_WEIGHT) / (count + PRIOR_WEIGHT);
  // Wider ramp so mediocre owner sentiment can't display as ~4 stars.
  // 1.0★ → 0%, 5.0★ → 100% (linear, no compression floor).
  const pct = ramp(posterior, 1.0, 5.0, 0, 100);
  return clamp100(pct);
}
export function reviewConfidence(count: number): "low" | "medium" | "high" {
  if (count >= 75) return "high";
  if (count >= 15) return "medium";
  return "low";
}

// Anchor reviews to engine-computed quality so the star rating can never
// drift far above what reliability / abuse-tolerance / build-quality say.
// For subwoofers especially, this prevents a poorly-built sub from
// displaying ~5 stars just because the category prior is high.
function qualityAnchoredPrior(p: Product): number {
  const r = computeRatings(p);
  const avgPct = (r.reliability.score + r.abuseTolerance.score + r.buildQuality.score) / 3;
  // Map 0-100% engine score → 1.0-5.0 stars
  return 1 + (clamp100(avgPct) / 100) * 4;
}

// Hard cap: stars should not exceed engine quality + a small enthusiasm
// bonus. Owners tend to rate slightly higher than measured quality, but
// not by 1.5+ stars.
function qualityCeiling(p: Product): number {
  const r = computeRatings(p);
  const avgPct = (r.reliability.score + r.abuseTolerance.score + r.buildQuality.score) / 3;
  const ceilingStars = (clamp100(avgPct) / 100) * 5 + 0.6; // +0.6★ owner-bias allowance
  return clamp100((Math.min(5, ceilingStars) / 5) * 100);
}

export function smoothLiveReviews(
  raw: { averageRating: number; reviewCount: number; sources?: UserReviewsData["sources"]; summary?: string; lastRefreshed?: string; },
  category: ProductCategory,
  product?: Product,
): UserReviewsData {
  const prior = product ? qualityAnchoredPrior(product) : undefined;
  let score = bayesianReviewScore(raw.averageRating, raw.reviewCount, category, prior);
  if (product) score = Math.min(score, qualityCeiling(product));
  return {
    score,
    averageRating: +raw.averageRating.toFixed(2),
    reviewCount: raw.reviewCount,
    confidence: reviewConfidence(raw.reviewCount),
    sources: raw.sources ?? [],
    summary: raw.summary,
    lastRefreshed: raw.lastRefreshed,
  };
}
export function fallbackUserReviews(p: Product): UserReviewsData {
  // Blend the DB-seeded sentiment with engine-computed quality so the
  // star rating reflects measurable reliability/abuse/build — not just
  // an optimistic seed value.
  const seedStars = clamp01(p.ratings.userReviews / 100) * 5;
  const anchor = qualityAnchoredPrior(p);
  const blendedStars = seedStars * 0.4 + anchor * 0.6;
  let score = bayesianReviewScore(blendedStars, 10, p.category, anchor);
  score = Math.min(score, qualityCeiling(p));
  return {
    score,
    averageRating: +blendedStars.toFixed(1),
    reviewCount: 0,
    confidence: "low",
    sources: [],
    summary: "Estimated baseline — calibrated against engine reliability/abuse/build scores.",
  };
}

// ═════════════════════════════════════════════════════════════════
// ADVANCED SUBWOOFER RATING SYSTEM
// ─────────────────────────────────────────────────────────────────
// Hybrid weighted model for enthusiasts, pro installers, SPL
// competitors, and SQ listeners. Eight tracked signals; owner
// reviews capped at 8% with explicit anti-manipulation guards
// (fake reviews, fanboy bias, marketing hype, bad-install bombing).
// ═════════════════════════════════════════════════════════════════

export interface SubwooferUseCaseScores {
  dailyDriver: number;
  splCompetition: number;
  soundQuality: number;
  burpDemo: number;
  budgetBuild: number;
  wallBuild: number;
}

export interface SubwooferAdvancedRating {
  overall: number;
  tier: "Reference" | "Excellent" | "Solid" | "Average" | "Weak";
  signals: {
    measuredPerformance: number;
    reliability: number;
    abuseTolerance: number;
    buildQuality: number;
    failureAnalysis: number;
    installerSignal: number;
    verifiedReviewTrust: number;
    valuePerPerformance: number;
  };
  useCases: SubwooferUseCaseScores;
  bestUseCase: keyof SubwooferUseCaseScores;
  reviewFlags: string[];
  summary: string;
}

// ─── Measured performance (pure physics, size-normalized) ──────────
// A great 8" sub should score as high as a great 18" — judge each
// driver against what is physically realistic for its size class.
// Anchors come from teardown / klippel data on flagship subs at each
// size (e.g. Stereo Integrity HST-18, Sundown ZV6-18, DC LV5 m1.5 12,
// JL 8W7, etc.). Anchors = "world-class for this size", priors below
// half of anchor = "weak for this size".
const SIZE_ANCHORS: Record<number, { rms: number; vdL: number; motorOz: number }> = {
  6.5: { rms: 350,  vdL: 0.20, motorOz: 60  },
  8:   { rms: 600,  vdL: 0.35, motorOz: 90  },
  10:  { rms: 1200, vdL: 0.70, motorOz: 140 },
  12:  { rms: 1800, vdL: 1.30, motorOz: 200 },
  15:  { rms: 2500, vdL: 2.20, motorOz: 280 },
  18:  { rms: 3500, vdL: 3.50, motorOz: 360 },
  21:  { rms: 4500, vdL: 4.80, motorOz: 440 },
  24:  { rms: 5500, vdL: 6.50, motorOz: 520 },
};
function sizeAnchor(sizeIn: number): { rms: number; vdL: number; motorOz: number } {
  if (sizeIn <= 0) return SIZE_ANCHORS[12];
  const keys = Object.keys(SIZE_ANCHORS).map(Number).sort((a, b) => a - b);
  if (sizeIn <= keys[0]) return SIZE_ANCHORS[keys[0]];
  if (sizeIn >= keys[keys.length - 1]) return SIZE_ANCHORS[keys[keys.length - 1]];
  for (let i = 0; i < keys.length - 1; i++) {
    if (sizeIn >= keys[i] && sizeIn <= keys[i + 1]) {
      const t = (sizeIn - keys[i]) / (keys[i + 1] - keys[i]);
      const a = SIZE_ANCHORS[keys[i]], b = SIZE_ANCHORS[keys[i + 1]];
      return { rms: a.rms + (b.rms - a.rms) * t,
               vdL: a.vdL + (b.vdL - a.vdL) * t,
               motorOz: a.motorOz + (b.motorOz - a.motorOz) * t };
    }
  }
  return SIZE_ANCHORS[12];
}
function subMeasuredPerformance(p: Product): number {
  if (p.category !== "subwoofer") return 50;
  const specs = p.specs as Record<string, string | number | undefined>;
  const rms = num(specs["RMS Power"]);
  const xmax = num(specs["Xmax"]);
  const sens = num(specs["Sensitivity"]);
  const vc = num(specs["Voice Coil"]);
  const size = num(specs["Size"] ?? specs["Diameter"]);
  const motor = num(specs["Magnet Weight"] ?? specs["Motor Weight"]);
  const anchor = sizeAnchor(size || 12);

  // Score each physical dimension relative to "world-class for this size".
  // 1.0× anchor = +pts; 0.5× = ~half pts; >1.2× = small over-the-top bonus.
  const relScore = (actual: number, ref: number, maxPts: number): number => {
    if (actual <= 0 || ref <= 0) return 0;
    const ratio = actual / ref;
    // Smooth ramp: 0→0, 0.5→0.45*max, 1.0→max, 1.4→1.08*max (capped).
    const pts = maxPts * (1 - Math.exp(-1.6 * ratio));
    return Math.min(maxPts * 1.12, pts);
  };

  let score = 45;
  score += relScore(rms, anchor.rms, 20);

  if (xmax > 0 && size > 0) {
    const sdCm2 = Math.PI * Math.pow((size * 2.54) / 2, 2) * 0.78;
    const vdLitres = (sdCm2 * xmax * 2) / 1000;
    score += relScore(vdLitres, anchor.vdL, 18);
  } else if (xmax > 0) {
    score += smoothUp(xmax, 18, 12);
  }

  if (motor > 0) score += relScore(motor, anchor.motorOz, 8);

  // Voice-coil mass / thermal capacity scales with driver size too.
  if (vc > 0 && size > 0) {
    const vcRef = size >= 15 ? 3.0 : size >= 12 ? 2.5 : size >= 10 ? 2.0 : 1.75;
    score += relScore(vc, vcRef, 8);
  } else if (vc > 0) {
    score += smoothUp(vc, 2.5, 8);
  }

  if (sens >= 88) score += ramp(sens, 88, 95, 0, 5);
  return clamp100(score);
}

// ─── Failure analysis (documented field failures only) ─────────────
function subFailureAnalysis(p: Product): number {
  let score = 80;
  const evidence = [
    p.evidence?.reliability,
    p.evidence?.abuseTolerance,
    p.evidence?.buildQuality,
  ].filter(Boolean).join(" ").toLowerCase();
  if (!evidence) return score;

  const heavy = [
    "coil burn", "voice coil failure", "vc burnout", "burns out",
    "former collapse", "former buckle", "demagnetiz",
    "spider tear", "spider rip", "surround tear", "cone separation",
    "lead wire break", "lead-wire", "tinsel break",
  ].filter((k) => evidence.includes(k)).length;
  const minor = [
    "rattle", "noisy", "buzz", "loose terminal",
    "shipping damage", "magnet shift", "occasional",
  ].filter((k) => evidence.includes(k)).length;
  score -= heavy * 8 + minor * 3;
  if (/many users|frequently|commonly|widespread|recurring/.test(evidence)) score -= 6;
  if (/no reported failures|bulletproof|never failed|years of abuse/.test(evidence)) score += 6;
  return clamp100(score);
}

// ─── Installer / forum-pro consensus (not random owners) ───────────
function subInstallerSignal(p: Product): number {
  const evidence = [
    p.evidence?.reliability,
    p.evidence?.abuseTolerance,
    p.evidence?.buildQuality,
  ].filter(Boolean).join(" ").toLowerCase();
  const brand = brandRep(p.brand);
  let score = 50 + (brand - 50) * 0.35;
  if (!evidence) return clamp100(score);
  const pos = [
    "installer favorite", "shop favorite", "go-to sub", "go to sub",
    "competitor choice", "spl winner", "competition proven",
    "highly recommended by", "consensus pick", "proven in",
    "battle tested", "tournament", "world record",
  ].filter((k) => evidence.includes(k)).length;
  const neg = [
    "installers avoid", "shop hates", "comeback", "warranty queen",
    "fanboy hype", "marketing", "overrated", "rebadged", "rebadge",
    "inflated specs", "doa", "dead on arrival",
  ].filter((k) => evidence.includes(k)).length;
  return clamp100(score + pos * 5 - neg * 6);
}

// ─── Verified-review trust (anti-manipulation guards) ──────────────
export function subVerifiedReviewTrust(
  p: Product,
  reviews?: { averageRating: number; reviewCount: number; sources?: { name: string; count?: number; rating?: number }[] },
): { score: number; flags: string[] } {
  const flags: string[] = [];
  if (!reviews || reviews.reviewCount <= 0) {
    flags.push("no verified reviews — using engine-anchored estimate");
    return { score: fallbackUserReviews(p).score, flags };
  }
  const sources = reviews.sources ?? [];
  const sourceCount = sources.length;
  const totalCount = reviews.reviewCount;
  const avg = clamp01(reviews.averageRating / 5) * 5;

  let diversityMult = 1.0;
  if (sourceCount >= 4) diversityMult = 1.15;
  else if (sourceCount >= 2) diversityMult = 1.05;
  else if (sourceCount <= 1 && totalCount < 30) {
    flags.push("single source — fake-review risk elevated");
    diversityMult = 0.85;
  }

  const trust: Record<string, number> = {
    "crutchfield": 1.0, "sonic electronix": 0.85, "best buy": 0.8,
    "amazon": 0.55, "ebay": 0.45, "walmart": 0.55,
    "diyma": 1.1, "smd forum": 1.1, "reddit": 0.75,
    "manufacturer": 0.5, "official site": 0.5,
  };
  let weightedSum = 0, weightTotal = 0;
  for (const s of sources) {
    const key = (s.name ?? "").toLowerCase();
    const t = Object.entries(trust).find(([k]) => key.includes(k))?.[1] ?? 0.7;
    const n = Math.max(1, s.count ?? 1);
    const r = clamp01((s.rating ?? avg) / 5) * 5;
    const wt = t * Math.log10(n + 9); // log-volume so a 10k marketplace doesn't drown a curated forum
    weightedSum += r * wt;
    weightTotal += wt;
  }
  const weightedAvg = weightTotal > 0 ? weightedSum / weightTotal : avg;

  if (avg >= 4.85 && totalCount < 25) flags.push("near-perfect rating with few reviews — possible inflation");
  const brand = brandRep(p.brand);
  if (brand < 50 && avg >= 4.5) flags.push("rating contradicts brand failure-rate evidence");
  const high = sources.filter((s) => (s.rating ?? avg) >= 4.7).length;
  const low  = sources.filter((s) => (s.rating ?? avg) <= 3.2).length;
  if (high >= 1 && low >= 1) flags.push("polarized reviews — likely setup-dependent");

  // Bayesian shrink — extra prior weight per guard flag.
  const enginePrior = qualityAnchoredPrior(p);
  const extraShrink = flags.length * 25;
  const effectiveCount = totalCount + extraShrink;
  const posterior = (weightedAvg * effectiveCount + enginePrior * PRIOR_WEIGHT) / (effectiveCount + PRIOR_WEIGHT);
  let pct = ramp(posterior, 1.0, 5.0, 0, 100) * diversityMult;

  // Forum consensus is capped ASYMMETRICALLY (-6 .. +3) because owner
  // sentiment is inherently skewed toward failure reports — satisfied
  // long-term owners rarely post. Negative signals get near-full weight;
  // positive signals are down-weighted (verified specs & installer
  // feedback do the heavy lifting on the upside).
  const forumBlob = [
    p.evidence?.reliability, p.evidence?.abuseTolerance,
    p.evidence?.buildQuality, p.evidence?.userReviews,
  ].filter(Boolean).join(" ");
  const fcOverall = subForumConsensus(forumBlob, "userReviews");
  if (fcOverall) {
    const capped = Math.max(-6, Math.min(3, fcOverall.delta));
    pct += capped;
    flags.push(capped >= 0
      ? `forum consensus boost (+${capped.toFixed(1)}, capped)`
      : `forum consensus drag (${capped.toFixed(1)})`);
  }

  // Positive-silence — flagship/mainstream install base with low complaint
  // volume counter-weights the failure-report base-rate bias above.
  const silence = positiveSilenceBonus(p);
  if (silence) {
    pct += silence.delta;
    flags.push(`${silence.label} (+${silence.delta})`);
  }

  return { score: Math.min(clamp100(pct), qualityCeiling(p)), flags };
}

// ─── Value per performance ─────────────────────────────────────────
function subValuePerPerformance(p: Product, measured: number): number {
  if (p.price <= 0) return 50;
  const rms = num(p.specs["RMS Power"]);
  const wpd = rms > 0 ? rms / p.price : 0;
  let value = 50;
  if (wpd >= 4) value += 20;
  else if (wpd >= 2) value += 12;
  else if (wpd >= 1) value += 6;
  else if (wpd >= 0.5) value += 2;
  else if (wpd < 0.25) value -= 8;
  value += (measured - 60) * 0.35;
  if (p.price >= 600 && measured >= 75) value = Math.max(value, 65);
  return clamp100(value);
}

// ─── Use-case rankings ─────────────────────────────────────────────
function computeSubwooferUseCases(
  p: Product,
  s: SubwooferAdvancedRating["signals"],
): SubwooferUseCaseScores {
  const specs = p.specs as Record<string, string | number | undefined>;
  const rms = num(specs["RMS Power"]);
  const xmax = num(specs["Xmax"]);
  const sens = num(specs["Sensitivity"]);
  const fs = num(specs["Fs"]);
  const qts = num(specs["Qts"]);

  const dailyDriver = clamp100(
    s.reliability * 0.30 + s.buildQuality * 0.20 + s.abuseTolerance * 0.10 +
    s.measuredPerformance * 0.20 + s.failureAnalysis * 0.20,
  );

  let splCompetition = s.measuredPerformance * 0.45 + s.abuseTolerance * 0.30 +
    s.buildQuality * 0.10 + s.failureAnalysis * 0.15;
  if (sens >= 86) splCompetition += 5;
  if (rms >= 2000) splCompetition += 4;
  splCompetition = clamp100(splCompetition);

  let soundQuality = s.buildQuality * 0.35 + s.reliability * 0.25 +
    s.measuredPerformance * 0.20 + s.failureAnalysis * 0.20;
  if (qts >= 0.35 && qts <= 0.55) soundQuality += 8;
  if (fs > 0 && fs <= 30) soundQuality += 4;
  if (sens > 0 && sens < 84 && rms >= 800) soundQuality -= 3;
  soundQuality = clamp100(soundQuality);

  let burpDemo = s.abuseTolerance * 0.40 + s.reliability * 0.20 +
    s.measuredPerformance * 0.25 + s.failureAnalysis * 0.15;
  const cooling = subCoolingScore(specs);
  if (cooling) burpDemo += Math.min(6, cooling.pts);
  burpDemo = clamp100(burpDemo);

  let budgetBuild = s.valuePerPerformance * 0.55 + s.reliability * 0.20 +
    s.measuredPerformance * 0.20 + s.failureAnalysis * 0.05;
  if (s.reliability < 45 || s.failureAnalysis < 50) budgetBuild *= 0.75;
  budgetBuild = clamp100(budgetBuild);

  let wallBuild = s.measuredPerformance * 0.35 + s.abuseTolerance * 0.30 +
    s.buildQuality * 0.20 + s.reliability * 0.15;
  if (rms < 1500) wallBuild *= 0.7;
  if (xmax < 19) wallBuild *= 0.85;
  wallBuild = clamp100(wallBuild);

  return { dailyDriver, splCompetition, soundQuality, burpDemo, budgetBuild, wallBuild };
}

// ─── Public entry point ────────────────────────────────────────────
export function computeSubwooferAdvancedRating(
  p: Product,
  reviews?: { averageRating: number; reviewCount: number; sources?: { name: string; count?: number; rating?: number }[] },
): SubwooferAdvancedRating {
  if (p.category !== "subwoofer") {
    const r = computeRatings(p);
    const fb = clamp100((r.reliability.score + r.abuseTolerance.score + r.buildQuality.score) / 3);
    return {
      overall: fb, tier: tierFor(fb),
      signals: { measuredPerformance: fb, reliability: r.reliability.score,
        abuseTolerance: r.abuseTolerance.score, buildQuality: r.buildQuality.score,
        failureAnalysis: 70, installerSignal: 50, verifiedReviewTrust: 50,
        valuePerPerformance: 50 },
      useCases: { dailyDriver: fb, splCompetition: fb, soundQuality: fb,
        burpDemo: fb, budgetBuild: fb, wallBuild: fb },
      bestUseCase: "dailyDriver", reviewFlags: [],
      summary: "Non-subwoofer category — generic fallback.",
    };
  }

  const r = computeRatings(p);
  const measuredPerformance = subMeasuredPerformance(p);
  const reliability = r.reliability.score;
  const abuseTolerance = r.abuseTolerance.score;
  const buildQuality = r.buildQuality.score;
  const failureAnalysis = subFailureAnalysis(p);
  const installerSignal = subInstallerSignal(p);
  const { score: verifiedReviewTrust, flags: reviewFlags } = subVerifiedReviewTrust(p, reviews);
  const valuePerPerformance = subValuePerPerformance(p, measuredPerformance);

  // Hybrid weights — sum = 1.00. Owner reviews intentionally capped at 8%.
  const overall = clamp100(
    measuredPerformance * 0.18 +
    reliability         * 0.20 +
    abuseTolerance      * 0.16 +
    buildQuality        * 0.14 +
    failureAnalysis     * 0.10 +
    installerSignal     * 0.08 +
    verifiedReviewTrust * 0.08 +
    valuePerPerformance * 0.06,
  );

  const signals = { measuredPerformance, reliability, abuseTolerance, buildQuality,
    failureAnalysis, installerSignal, verifiedReviewTrust, valuePerPerformance };
  const useCases = computeSubwooferUseCases(p, signals);
  const bestUseCase = (Object.entries(useCases)
    .sort(([, a], [, b]) => b - a)[0][0]) as keyof SubwooferUseCaseScores;

  return { overall, tier: tierFor(overall), signals, useCases, bestUseCase,
    reviewFlags, summary: subAdvancedSummary(overall, bestUseCase, reviewFlags.length) };
}

function tierFor(n: number): SubwooferAdvancedRating["tier"] {
  if (n >= 88) return "Reference";
  if (n >= 78) return "Excellent";
  if (n >= 65) return "Solid";
  if (n >= 50) return "Average";
  return "Weak";
}
const USE_CASE_LABEL: Record<keyof SubwooferUseCaseScores, string> = {
  dailyDriver: "daily-driver musical use",
  splCompetition: "SPL competition",
  soundQuality: "SQ / sound-quality listening",
  burpDemo: "demo / burp sessions",
  budgetBuild: "budget builds",
  wallBuild: "wall / high-power competition rigs",
};
function subAdvancedSummary(overall: number, best: keyof SubwooferUseCaseScores, flags: number): string {
  const tier = tierFor(overall);
  const base = tier === "Reference" ? "Reference-grade sub."
    : tier === "Excellent" ? "Excellent across the board."
    : tier === "Solid" ? "Solid all-rounder."
    : tier === "Average" ? "Average performer — picks its battles."
    : "Below-average — better options exist at this price.";
  return `${base} Best suited for ${USE_CASE_LABEL[best]}.${flags > 0 ? ` Owner-review trust reduced (${flags} guard flag${flags > 1 ? "s" : ""}).` : ""}`;
}

// ═════════════════════════════════════════════════════════════════
// ADVANCED AMPLIFIER RATING SYSTEM
// ─────────────────────────────────────────────────────────────────
// Mirrors the 8-signal hybrid model used for subwoofers, tuned for
// what actually breaks an amp: thermal runaway, PSU sag, FET failure,
// and inflated power claims. Owner reviews capped at 8% with the
// same anti-manipulation guards (fake reviews, fanboy hype, bad
// install bombing).
// ═════════════════════════════════════════════════════════════════

export interface AmplifierUseCaseScores {
  dailyDriver: number;       // long-term musical use
  splCompetition: number;    // peak burp current delivery
  soundQuality: number;      // low THD, A/B or premium D, regulated PSU
  sustainedBurp: number;     // 30+ second tones — thermal mass & cooling
  budgetBuild: number;       // watts/$ with a reliability floor
  monoFullPower: number;     // wall / dedicated sub channel at 1ohm or below
}

export interface AmplifierAdvancedRating {
  overall: number;
  tier: "Reference" | "Excellent" | "Solid" | "Average" | "Weak";
  signals: {
    measuredPerformance: number;
    reliability: number;
    abuseTolerance: number;
    buildQuality: number;
    failureAnalysis: number;
    installerSignal: number;
    verifiedReviewTrust: number;
    valuePerPerformance: number;
  };
  useCases: AmplifierUseCaseScores;
  bestUseCase: keyof AmplifierUseCaseScores;
  reviewFlags: string[];
  summary: string;
}

// ─── Measured performance ────────────────────────────────────────
// Pure honesty-of-spec score. Rewards: impedance doubling, fuse
// matching real current draw, watts/lb in the believable zone,
// rated stability at 1ohm or lower, regulated PSU, low THD.
function ampMeasuredPerformance(p: Product): number {
  if (p.category !== "amplifier") return 50;
  const specs = p.specs as Record<string, string | number | undefined>;
  const rms = totalAmpRms(specs);
  const fuse = totalFuseAmps(specs["Fuse Rating"]);
  const weight = num(specs["Weight"]);
  const eff = num(specs["Efficiency"]);
  const thd = num(specs["THD"]);
  const ladder = ampImpedanceLadder(specs);
  const topology = (specs["Topology"] ?? specs["Class"] ?? "").toString().toLowerCase();
  const isClassD = topology.includes("d") && !topology.includes("ab");
  const assumedEff = eff > 0 ? eff : isClassD ? 85 : 65;

  let score = 50;

  // Honest power: fuse vs real music-program current draw
  if (fuse > 0 && rms > 0) {
    const musicAmps = (rms / (assumedEff / 100) / 13.8) * 0.40;
    const ratio = fuse / musicAmps;
    if (ratio < 0.55) score -= 14;
    else if (ratio < 0.75) score -= 7;
    else if (ratio >= 0.85 && ratio <= 1.6) score += 10;
    else if (ratio > 1.6 && ratio <= 2.5) score += 6;
  }

  // Impedance doubling — proxy for real PSU capability
  if (ladder.length >= 2) {
    const low = ladder[0], high = ladder[ladder.length - 1];
    if (high.ohm > 0 && low.ohm > high.ohm && low.watts > 0) {
      const coherence = (high.watts / low.watts) / (low.ohm / high.ohm);
      if (coherence >= 0.85 && coherence <= 1.15) score += 12;
      else if (coherence >= 0.70) score += 6;
      else if (coherence < 0.55) score -= 10;
    }
  }

  // Watts-per-pound — topology-aware, not universal thresholds.
  const wppTop = wppPlausibility(rms, weight, ampTopologyClass(specs), fuse);
  if (wppTop) {
    if (wppTop.verdict === "generous") score += 8;
    else if (wppTop.verdict === "healthy") score += 4;
    else if (wppTop.verdict === "improbable") score -= 10;
  }

  // Bench-test measured RMS overrides rated when discrepancy > 10%
  const bench = getBenchTest(p.id);
  if (bench?.measuredRmsWatts && rms > 0) {
    const ratio = bench.measuredRmsWatts / rms;
    if (ratio >= 1.0) score += 8;              // measured meets/exceeds
    else if (ratio >= 0.90) score += 2;
    else if (ratio >= 0.75) score -= 6;
    else score -= 15;                          // significantly inflated
  }
  if (bench?.sustainedLoadHoldPct !== undefined) {
    if (bench.sustainedLoadHoldPct >= 90) score += 6;
    else if (bench.sustainedLoadHoldPct < 75) score -= 6;
  }

  // Low-impedance engineering
  if (ladder.length > 0) {
    const lowestZ = ladder[ladder.length - 1].ohm;
    if (lowestZ <= 0.5) score += 6;
    else if (lowestZ <= 1) score += 4;
    else if (lowestZ <= 2) score += 2;
  }

  // Engineering tightness
  if (thd > 0 && thd < 0.05) score += 4;
  else if (thd >= 0.5) score -= 3;
  if (eff >= 88) score += 4;

  // CEA-2006 / dyno / birth-sheet
  const certs = JSON.stringify(specs).toLowerCase();
  if (/cea[-\s]?2006/.test(certs)) score += 5;
  if (/birth sheet|dyno tested|individually tested/.test(certs)) score += 5;
  if (/regulated|smps|switching/.test((specs["Power Supply"] ?? specs["PSU"] ?? "").toString().toLowerCase())) score += 3;

  return clamp100(score);
}

// ─── Failure analysis (documented amp field failures) ────────────
function ampFailureAnalysis(p: Product): number {
  let score = 80;
  const evidence = [
    p.evidence?.reliability, p.evidence?.abuseTolerance, p.evidence?.buildQuality,
  ].filter(Boolean).join(" ").toLowerCase();
  if (!evidence) return score;

  const heavy = [
    "fet failure", "fet blow", "output stage failure", "blown output",
    "psu failure", "power supply failure", "transformer failure",
    "thermal runaway", "catches fire", "burns up", "smokes",
    "common failure", "frequent failures", "recurring failure",
    "high doa", "dead on arrival", "comeback queen", "warranty queen",
  ].filter((k) => evidence.includes(k)).length;
  const minor = [
    "fan noise", "thermal trip", "thermals trip", "shuts down hot",
    "rattle", "loose terminal", "remote turn-on", "ground loop",
    "shipping damage", "occasional",
  ].filter((k) => evidence.includes(k)).length;
  score -= heavy * 9 + minor * 3;
  if (/many users|frequently|commonly|widespread|recurring/.test(evidence)) score -= 6;
  if (/no reported failures|bulletproof|never failed|years of abuse/.test(evidence)) score += 6;
  return clamp100(score);
}

// ─── Installer / forum-pro consensus ─────────────────────────────
function ampInstallerSignal(p: Product): number {
  const evidence = [
    p.evidence?.reliability, p.evidence?.abuseTolerance, p.evidence?.buildQuality,
  ].filter(Boolean).join(" ").toLowerCase();
  const brand = brandRep(p.brand);
  let score = 50 + (brand - 50) * 0.35;
  if (!evidence) return clamp100(score);
  const pos = [
    "installer favorite", "shop favorite", "go-to amp", "go to amp",
    "competitor choice", "spl winner", "competition proven", "spl proven",
    "highly recommended by", "consensus pick", "battle tested", "battle-tested",
    "tournament", "world record", "shop standard",
  ].filter((k) => evidence.includes(k)).length;
  const neg = [
    "installers avoid", "shop hates", "comeback", "warranty queen",
    "fanboy hype", "marketing hype", "overrated", "rebadged", "rebadge",
    "inflated specs", "underrated power", "doa", "dead on arrival",
    "doesn't make rated power", "does not make rated power",
  ].filter((k) => evidence.includes(k)).length;
  return clamp100(score + pos * 5 - neg * 6);
}

// ─── Verified-review trust (anti-manipulation guards) ────────────
export function ampVerifiedReviewTrust(
  p: Product,
  reviews?: { averageRating: number; reviewCount: number; sources?: { name: string; count?: number; rating?: number }[] },
): { score: number; flags: string[] } {
  const flags: string[] = [];
  if (!reviews || reviews.reviewCount <= 0) {
    flags.push("no verified reviews — using engine-anchored estimate");
    return { score: fallbackUserReviews(p).score, flags };
  }
  const sources = reviews.sources ?? [];
  const sourceCount = sources.length;
  const totalCount = reviews.reviewCount;
  const avg = clamp01(reviews.averageRating / 5) * 5;

  let diversityMult = 1.0;
  if (sourceCount >= 4) diversityMult = 1.15;
  else if (sourceCount >= 2) diversityMult = 1.05;
  else if (sourceCount <= 1 && totalCount < 30) {
    flags.push("single source — fake-review risk elevated");
    diversityMult = 0.85;
  }

  const trust: Record<string, number> = {
    "crutchfield": 1.0, "sonic electronix": 0.85, "best buy": 0.8,
    "amazon": 0.55, "ebay": 0.45, "walmart": 0.55,
    "diyma": 1.1, "smd forum": 1.1, "reddit": 0.75,
    "manufacturer": 0.5, "official site": 0.5,
  };
  let weightedSum = 0, weightTotal = 0;
  for (const s of sources) {
    const key = (s.name ?? "").toLowerCase();
    const t = Object.entries(trust).find(([k]) => key.includes(k))?.[1] ?? 0.7;
    const n = Math.max(1, s.count ?? 1);
    const r = clamp01((s.rating ?? avg) / 5) * 5;
    const wt = t * Math.log10(n + 9);
    weightedSum += r * wt;
    weightTotal += wt;
  }
  const weightedAvg = weightTotal > 0 ? weightedSum / weightTotal : avg;

  if (avg >= 4.85 && totalCount < 25) flags.push("near-perfect rating with few reviews — possible inflation");
  const brand = brandRep(p.brand);
  if (brand < 50 && avg >= 4.5) flags.push("rating contradicts brand failure-rate evidence");
  const high = sources.filter((s) => (s.rating ?? avg) >= 4.7).length;
  const low  = sources.filter((s) => (s.rating ?? avg) <= 3.2).length;
  if (high >= 1 && low >= 1) flags.push("polarized reviews — likely setup-dependent (under/over-powered installs)");

  const enginePrior = qualityAnchoredPrior(p);
  const extraShrink = flags.length * 25;
  const effectiveCount = totalCount + extraShrink;
  const posterior = (weightedAvg * effectiveCount + enginePrior * PRIOR_WEIGHT) / (effectiveCount + PRIOR_WEIGHT);
  let pct = ramp(posterior, 1.0, 5.0, 0, 100) * diversityMult;

  const forumBlob = [
    p.evidence?.reliability, p.evidence?.abuseTolerance,
    p.evidence?.buildQuality, p.evidence?.userReviews,
  ].filter(Boolean).join(" ");
  const fcOverall = subForumConsensus(forumBlob, "userReviews");
  if (fcOverall) {
    // Asymmetric cap — see subVerifiedReviewTrust for rationale.
    const capped = Math.max(-6, Math.min(3, fcOverall.delta));
    pct += capped;
    flags.push(capped >= 0
      ? `forum consensus boost (+${capped.toFixed(1)}, capped)`
      : `forum consensus drag (${capped.toFixed(1)})`);
  }
  const silence = positiveSilenceBonus(p);
  if (silence) {
    pct += silence.delta;
    flags.push(`${silence.label} (+${silence.delta})`);
  }

  return { score: Math.min(clamp100(pct), qualityCeiling(p)), flags };
}

// ─── Value per performance ───────────────────────────────────────
function ampValuePerPerformance(p: Product, measured: number, reliability: number): number {
  if (p.price <= 0) return 50;
  const rms = totalAmpRms(p.specs as Record<string, string | number | undefined>);
  const wpd = rms > 0 ? rms / p.price : 0;
  let value = 50;
  if (wpd >= 5) value += 22;
  else if (wpd >= 3) value += 14;
  else if (wpd >= 1.5) value += 8;
  else if (wpd >= 0.75) value += 3;
  else if (wpd < 0.35) value -= 8;
  value += (measured - 60) * 0.30;
  // Cheap watts mean nothing if it dies in 6 months
  if (reliability < 50) value *= 0.80;
  if (p.price >= 600 && measured >= 75) value = Math.max(value, 65);
  return clamp100(value);
}

// ─── Use-case rankings ───────────────────────────────────────────
function computeAmplifierUseCases(
  p: Product,
  s: AmplifierAdvancedRating["signals"],
): AmplifierUseCaseScores {
  const specs = p.specs as Record<string, string | number | undefined>;
  const rms = totalAmpRms(specs);
  const weight = num(specs["Weight"]);
  const eff = num(specs["Efficiency"]);
  const thd = num(specs["THD"]);
  const ladder = ampImpedanceLadder(specs);
  const lowestZ = ladder.length ? ladder[ladder.length - 1].ohm : 4;
  const topology = (specs["Topology"] ?? specs["Class"] ?? "").toString().toLowerCase();
  const isAB = topology.includes("ab") || topology.includes("a/b") || topology.includes("gh");
  const cooling = (specs["Cooling"] ?? "").toString().toLowerCase();
  const hasFan = cooling.includes("fan") || cooling.includes("active");
  const wpp = rms > 0 && weight > 0 ? rms / weight : 200;

  const dailyDriver = clamp100(
    s.reliability * 0.32 + s.buildQuality * 0.18 + s.failureAnalysis * 0.20 +
    s.measuredPerformance * 0.18 + s.abuseTolerance * 0.12,
  );

  let splCompetition = s.abuseTolerance * 0.32 + s.measuredPerformance * 0.30 +
    s.buildQuality * 0.13 + s.failureAnalysis * 0.15 + s.reliability * 0.10;
  if (lowestZ <= 1) splCompetition += 5;
  if (rms >= 2500) splCompetition += 4;
  splCompetition = clamp100(splCompetition);

  let soundQuality = s.buildQuality * 0.30 + s.reliability * 0.25 +
    s.measuredPerformance * 0.25 + s.failureAnalysis * 0.20;
  if (isAB) soundQuality += 5;
  if (thd > 0 && thd < 0.05) soundQuality += 5;
  if (eff > 0 && eff < 70 && rms > 500) soundQuality -= 2;
  soundQuality = clamp100(soundQuality);

  let sustainedBurp = s.abuseTolerance * 0.38 + s.measuredPerformance * 0.22 +
    s.failureAnalysis * 0.18 + s.buildQuality * 0.12 + s.reliability * 0.10;
  if (wpp < 150) sustainedBurp += 5;
  if (wpp > 320) sustainedBurp -= 6;
  if (hasFan) sustainedBurp += 4;
  if (eff >= 88) sustainedBurp += 3;
  sustainedBurp = clamp100(sustainedBurp);

  let budgetBuild = s.valuePerPerformance * 0.55 + s.reliability * 0.20 +
    s.measuredPerformance * 0.20 + s.failureAnalysis * 0.05;
  if (s.reliability < 45 || s.failureAnalysis < 50) budgetBuild *= 0.75;
  budgetBuild = clamp100(budgetBuild);

  let monoFullPower = s.measuredPerformance * 0.32 + s.abuseTolerance * 0.30 +
    s.buildQuality * 0.18 + s.reliability * 0.10 + s.failureAnalysis * 0.10;
  if (rms < 1500) monoFullPower *= 0.75;
  if (lowestZ > 1) monoFullPower *= 0.85;
  monoFullPower = clamp100(monoFullPower);

  return { dailyDriver, splCompetition, soundQuality, sustainedBurp, budgetBuild, monoFullPower };
}

const AMP_USE_CASE_LABEL: Record<keyof AmplifierUseCaseScores, string> = {
  dailyDriver: "daily-driver musical use",
  splCompetition: "SPL burp competition",
  soundQuality: "SQ / sound-quality systems",
  sustainedBurp: "sustained high-power burps and demos",
  budgetBuild: "budget builds",
  monoFullPower: "wall / dedicated full-power mono",
};

function ampAdvancedSummary(overall: number, best: keyof AmplifierUseCaseScores, flags: number): string {
  const tier = tierFor(overall);
  const base = tier === "Reference" ? "Reference-grade amplifier."
    : tier === "Excellent" ? "Excellent across the board."
    : tier === "Solid" ? "Solid all-rounder."
    : tier === "Average" ? "Average performer — picks its battles."
    : "Below-average — better options exist at this price.";
  return `${base} Best suited for ${AMP_USE_CASE_LABEL[best]}.${flags > 0 ? ` Owner-review trust reduced (${flags} guard flag${flags > 1 ? "s" : ""}).` : ""}`;
}

// ─── Public entry point ──────────────────────────────────────────
export function computeAmplifierAdvancedRating(
  p: Product,
  reviews?: { averageRating: number; reviewCount: number; sources?: { name: string; count?: number; rating?: number }[] },
): AmplifierAdvancedRating {
  if (p.category !== "amplifier") {
    const r = computeRatings(p);
    const fb = clamp100((r.reliability.score + r.abuseTolerance.score + r.buildQuality.score) / 3);
    return {
      overall: fb, tier: tierFor(fb),
      signals: { measuredPerformance: fb, reliability: r.reliability.score,
        abuseTolerance: r.abuseTolerance.score, buildQuality: r.buildQuality.score,
        failureAnalysis: 70, installerSignal: 50, verifiedReviewTrust: 50,
        valuePerPerformance: 50 },
      useCases: { dailyDriver: fb, splCompetition: fb, soundQuality: fb,
        sustainedBurp: fb, budgetBuild: fb, monoFullPower: fb },
      bestUseCase: "dailyDriver", reviewFlags: [],
      summary: "Non-amplifier category — generic fallback.",
    };
  }

  const r = computeRatings(p);
  const measuredPerformance = ampMeasuredPerformance(p);
  const reliability = r.reliability.score;
  const abuseTolerance = r.abuseTolerance.score;
  const buildQuality = r.buildQuality.score;
  const failureAnalysis = ampFailureAnalysis(p);
  const installerSignal = ampInstallerSignal(p);
  const { score: verifiedReviewTrust, flags: reviewFlags } = ampVerifiedReviewTrust(p, reviews);
  const valuePerPerformance = ampValuePerPerformance(p, measuredPerformance, reliability);

  // Hybrid weights — sum = 1.00. Owner reviews intentionally capped at 8%.
  const overall = clamp100(
    measuredPerformance * 0.18 +
    reliability         * 0.22 +
    abuseTolerance      * 0.16 +
    buildQuality        * 0.12 +
    failureAnalysis     * 0.12 +
    installerSignal     * 0.06 +
    verifiedReviewTrust * 0.08 +
    valuePerPerformance * 0.06,
  );

  const signals = { measuredPerformance, reliability, abuseTolerance, buildQuality,
    failureAnalysis, installerSignal, verifiedReviewTrust, valuePerPerformance };
  const useCases = computeAmplifierUseCases(p, signals);
  const bestUseCase = (Object.entries(useCases)
    .sort(([, a], [, b]) => b - a)[0][0]) as keyof AmplifierUseCaseScores;

  return { overall, tier: tierFor(overall), signals, useCases, bestUseCase,
    reviewFlags, summary: ampAdvancedSummary(overall, bestUseCase, reviewFlags.length) };
}

// ═════════════════════════════════════════════════════════════════
// ADVANCED SPEAKER RATING SYSTEM
// ─────────────────────────────────────────────────────────────────
// Hybrid 8-signal model tuned for what actually matters in a car
// speaker (coaxial or component): sensitivity (head-unit power is
// 18W/ch real-world), real RMS thermal handling, tweeter material
// & dome size, surround/spider longevity, mounting depth realism,
// frequency response width, and field-failure evidence. Owner
// reviews capped at 8% with the same anti-manipulation guards used
// on subs and amps.
// ═════════════════════════════════════════════════════════════════

export interface SpeakerUseCaseScores {
  dailyDriver: number;        // long-term musical use off head-unit power
  soundQuality: number;       // SQ — tweeter material, FR width, low THD
  highPowerSPL: number;       // amplified, sustained-volume survival
  factoryReplacement: number; // drop-in fitment + factory-power friendly
  budgetBuild: number;        // value with a reliability floor
  midbassImpact: number;      // 6.5"/6x9" punch for kick / door builds
}

export interface SpeakerAdvancedRating {
  overall: number;
  tier: "Reference" | "Excellent" | "Solid" | "Average" | "Weak";
  signals: {
    measuredPerformance: number;
    reliability: number;
    abuseTolerance: number;
    buildQuality: number;
    failureAnalysis: number;
    installerSignal: number;
    verifiedReviewTrust: number;
    valuePerPerformance: number;
  };
  useCases: SpeakerUseCaseScores;
  bestUseCase: keyof SpeakerUseCaseScores;
  reviewFlags: string[];
  summary: string;
}

// ─── Size anchors (world-class RMS for size class) ────────────────
const SPEAKER_SIZE_ANCHORS: Record<number, { rms: number; sens: number }> = {
  3.5: { rms: 50,  sens: 88 },
  4:   { rms: 75,  sens: 88 },
  5.25:{ rms: 110, sens: 89 },
  6.5: { rms: 150, sens: 90 },
  6.75:{ rms: 160, sens: 90 },
  8:   { rms: 250, sens: 91 },
};
function speakerSizeAnchor(sizeIn: number): { rms: number; sens: number } {
  if (sizeIn <= 0) return SPEAKER_SIZE_ANCHORS[6.5];
  const keys = Object.keys(SPEAKER_SIZE_ANCHORS).map(Number).sort((a, b) => a - b);
  if (sizeIn <= keys[0]) return SPEAKER_SIZE_ANCHORS[keys[0]];
  if (sizeIn >= keys[keys.length - 1]) return SPEAKER_SIZE_ANCHORS[keys[keys.length - 1]];
  for (let i = 0; i < keys.length - 1; i++) {
    if (sizeIn >= keys[i] && sizeIn <= keys[i + 1]) {
      const t = (sizeIn - keys[i]) / (keys[i + 1] - keys[i]);
      const a = SPEAKER_SIZE_ANCHORS[keys[i]], b = SPEAKER_SIZE_ANCHORS[keys[i + 1]];
      return { rms: a.rms + (b.rms - a.rms) * t, sens: a.sens + (b.sens - a.sens) * t };
    }
  }
  return SPEAKER_SIZE_ANCHORS[6.5];
}

// Parse size — handles 6.5", 6x9, 5-1/4, etc.
function parseSpeakerSize(specs: Record<string, string | number | undefined>): number {
  const raw = (specs["Size"] ?? specs["Diameter"] ?? specs["Woofer Size"] ?? "").toString();
  if (!raw) return 0;
  // 6x9 → treat as 7.5 equivalent surface area class
  if (/6\s*[x×]\s*9/i.test(raw)) return 6.75;
  // Mixed fractions like 5-1/4
  const frac = raw.match(/(\d+)\s*[-]\s*(\d+)\s*\/\s*(\d+)/);
  if (frac) return +frac[1] + +frac[2] / +frac[3];
  const m = raw.match(/(\d+(?:\.\d+)?)/);
  return m ? +m[1] : 0;
}

function parseFrequencyResponse(specs: Record<string, string | number | undefined>): { low: number; high: number } {
  const raw = (specs["Frequency Response"] ?? specs["Freq Response"] ?? "").toString();
  const m = raw.match(/(\d+(?:\.\d+)?)\s*(?:hz)?\s*[-–to]+\s*(\d+(?:\.\d+)?)\s*(k?hz?)?/i);
  if (!m) return { low: 0, high: 0 };
  let high = +m[2];
  if (/k/i.test(m[3] ?? "") || high < 1000) high *= /k/i.test(m[3] ?? "") ? 1000 : 1;
  return { low: +m[1], high };
}

// ─── Measured performance ────────────────────────────────────────
// Sensitivity-first (since most head units deliver 15-22W real-world),
// then size-normalized RMS, tweeter quality, and FR extension.
function speakerMeasuredPerformance(p: Product): number {
  if (p.category !== "speaker") return 50;
  const specs = p.specs as Record<string, string | number | undefined>;
  const rms = num(specs["RMS Power"]);
  const peak = num(specs["Peak Power"]);
  const sens = num(specs["Sensitivity"]);
  const size = parseSpeakerSize(specs);
  const anchor = speakerSizeAnchor(size || 6.5);
  const fr = parseFrequencyResponse(specs);

  let score = 45;

  // Sensitivity — single biggest predictor of subjective loudness off
  // a head unit. +1 dB ≈ +25% perceived volume at the same wattage.
  if (sens > 0) {
    if (sens >= 93) score += 18;
    else if (sens >= 90) score += 14;
    else if (sens >= 88) score += 9;
    else if (sens >= 86) score += 4;
    else if (sens < 84) score -= 6;
  }

  // Size-normalized RMS handling
  if (rms > 0) {
    const ratio = rms / anchor.rms;
    const pts = 14 * (1 - Math.exp(-1.5 * ratio));
    score += Math.min(16, pts);
  }

  // Honest peak/RMS — > 3.5× is marketing fiction
  if (peak > 0 && rms > 0) {
    const r = peak / rms;
    if (r > 3.5) score -= 8;
    else if (r >= 1.8 && r <= 2.5) score += 4;
  }

  // Tweeter contribution — FR flatness + breakup is primary; material
  // is a tiebreaker only. A well-executed silk dome with a high breakup
  // point can outscore a poorly-implemented beryllium.
  const tweeterPerf = tweeterPerformanceScore(p);
  score += Math.round((tweeterPerf.score - 50) * 0.16);
  score += tweeterMaterialTiebreak(p) * 0.5;

  // Frequency response extension — wide & flat is what SQ shops chase
  if (fr.low > 0 && fr.high > 0) {
    if (fr.low <= 45 && fr.high >= 22000) score += 6;
    else if (fr.low <= 60 && fr.high >= 20000) score += 4;
    else if (fr.high < 18000) score -= 3;
  }

  // Construction signal — component sets and crossover-equipped pairs
  // are engineered with real time-alignment & passive networks.
  const blob = JSON.stringify(specs).toLowerCase();
  if (blob.includes("component")) score += 4;
  if (blob.includes("crossover")) score += 2;

  return clamp100(score);
}

// ─── Failure analysis ────────────────────────────────────────────
function speakerFailureAnalysis(p: Product): number {
  let score = 80;
  const evidence = [
    p.evidence?.reliability, p.evidence?.abuseTolerance, p.evidence?.buildQuality,
  ].filter(Boolean).join(" ").toLowerCase();
  if (!evidence) return score;

  const heavy = [
    "voice coil burn", "voice coil failure", "vc burnout", "coil burnout",
    "tweeter failure", "tweeter fries", "tweeter dies", "blown tweeter",
    "surround tear", "surround rot", "spider tear", "cone separation",
    "lead wire break", "tinsel break",
    "common failure", "frequent failures", "recurring failure",
    "high doa", "dead on arrival",
  ].filter((k) => evidence.includes(k)).length;
  const minor = [
    "harsh", "sibilant", "fatiguing", "rattle", "buzz",
    "loose terminal", "shipping damage", "occasional",
  ].filter((k) => evidence.includes(k)).length;
  score -= heavy * 9 + minor * 3;
  if (/many users|frequently|commonly|widespread|recurring/.test(evidence)) score -= 6;
  if (/no reported failures|bulletproof|never failed|years of abuse/.test(evidence)) score += 6;
  return clamp100(score);
}

// ─── Installer / forum-pro consensus ─────────────────────────────
function speakerInstallerSignal(p: Product): number {
  const evidence = [
    p.evidence?.reliability, p.evidence?.abuseTolerance, p.evidence?.buildQuality,
  ].filter(Boolean).join(" ").toLowerCase();
  const brand = brandRep(p.brand);
  let score = 50 + (brand - 50) * 0.35;
  if (!evidence) return clamp100(score);
  const pos = [
    "installer favorite", "shop favorite", "go-to speaker", "go to speaker",
    "sq winner", "iasca winner", "meca winner", "competition proven",
    "highly recommended by", "consensus pick", "reference set",
    "tonally accurate", "tonal accuracy", "imaging", "staging",
  ].filter((k) => evidence.includes(k)).length;
  const neg = [
    "installers avoid", "shop hates", "comeback", "warranty queen",
    "fanboy hype", "marketing hype", "overrated", "rebadged", "rebadge",
    "inflated specs", "harsh tweeter", "fatiguing",
    "doa", "dead on arrival",
  ].filter((k) => evidence.includes(k)).length;
  return clamp100(score + pos * 5 - neg * 6);
}

// ─── Verified-review trust (anti-manipulation guards) ────────────
export function speakerVerifiedReviewTrust(
  p: Product,
  reviews?: { averageRating: number; reviewCount: number; sources?: { name: string; count?: number; rating?: number }[] },
): { score: number; flags: string[] } {
  const flags: string[] = [];
  if (!reviews || reviews.reviewCount <= 0) {
    flags.push("no verified reviews — using engine-anchored estimate");
    return { score: fallbackUserReviews(p).score, flags };
  }
  const sources = reviews.sources ?? [];
  const sourceCount = sources.length;
  const totalCount = reviews.reviewCount;
  const avg = clamp01(reviews.averageRating / 5) * 5;

  let diversityMult = 1.0;
  if (sourceCount >= 4) diversityMult = 1.15;
  else if (sourceCount >= 2) diversityMult = 1.05;
  else if (sourceCount <= 1 && totalCount < 30) {
    flags.push("single source — fake-review risk elevated");
    diversityMult = 0.85;
  }

  const trust: Record<string, number> = {
    "crutchfield": 1.0, "sonic electronix": 0.85, "best buy": 0.8,
    "amazon": 0.55, "ebay": 0.45, "walmart": 0.55,
    "diyma": 1.1, "smd forum": 1.1, "reddit": 0.75,
    "manufacturer": 0.5, "official site": 0.5,
  };
  let weightedSum = 0, weightTotal = 0;
  for (const s of sources) {
    const key = (s.name ?? "").toLowerCase();
    const t = Object.entries(trust).find(([k]) => key.includes(k))?.[1] ?? 0.7;
    const n = Math.max(1, s.count ?? 1);
    const r = clamp01((s.rating ?? avg) / 5) * 5;
    const wt = t * Math.log10(n + 9);
    weightedSum += r * wt;
    weightTotal += wt;
  }
  const weightedAvg = weightTotal > 0 ? weightedSum / weightTotal : avg;

  if (avg >= 4.85 && totalCount < 25) flags.push("near-perfect rating with few reviews — possible inflation");
  const brand = brandRep(p.brand);
  if (brand < 50 && avg >= 4.5) flags.push("rating contradicts brand failure-rate evidence");
  const high = sources.filter((s) => (s.rating ?? avg) >= 4.7).length;
  const low  = sources.filter((s) => (s.rating ?? avg) <= 3.2).length;
  if (high >= 1 && low >= 1) flags.push("polarized reviews — likely setup-dependent (under-powered or clipped)");

  const enginePrior = qualityAnchoredPrior(p);
  const extraShrink = flags.length * 25;
  const effectiveCount = totalCount + extraShrink;
  const posterior = (weightedAvg * effectiveCount + enginePrior * PRIOR_WEIGHT) / (effectiveCount + PRIOR_WEIGHT);
  let pct = ramp(posterior, 1.0, 5.0, 0, 100) * diversityMult;

  const forumBlob = [
    p.evidence?.reliability, p.evidence?.abuseTolerance,
    p.evidence?.buildQuality, p.evidence?.userReviews,
  ].filter(Boolean).join(" ");
  const fcOverall = subForumConsensus(forumBlob, "userReviews");
  if (fcOverall) {
    // Asymmetric cap — see subVerifiedReviewTrust for rationale.
    const capped = Math.max(-6, Math.min(3, fcOverall.delta));
    pct += capped;
    flags.push(capped >= 0
      ? `forum consensus boost (+${capped.toFixed(1)}, capped)`
      : `forum consensus drag (${capped.toFixed(1)})`);
  }
  const silence = positiveSilenceBonus(p);
  if (silence) {
    pct += silence.delta;
    flags.push(`${silence.label} (+${silence.delta})`);
  }

  return { score: Math.min(clamp100(pct), qualityCeiling(p)), flags };
}

// ─── Value per performance ───────────────────────────────────────
function speakerValuePerPerformance(p: Product, measured: number, reliability: number): number {
  if (p.price <= 0) return 50;
  let value = 50;
  // Speakers are priced per pair vs single — but our DB stores list price
  // as-listed. Anchor against measured quality / price tier.
  if (p.price <= 80 && measured >= 65) value += 18;
  else if (p.price <= 150 && measured >= 72) value += 14;
  else if (p.price <= 300 && measured >= 78) value += 10;
  else if (p.price <= 600 && measured >= 82) value += 6;
  else if (p.price > 600 && measured < 78) value -= 8;
  value += (measured - 60) * 0.30;
  if (reliability < 50) value *= 0.80;
  if (p.price >= 500 && measured >= 80) value = Math.max(value, 65);
  return clamp100(value);
}

// ─── Use-case rankings ───────────────────────────────────────────
function computeSpeakerUseCases(
  p: Product,
  s: SpeakerAdvancedRating["signals"],
): SpeakerUseCaseScores {
  const specs = p.specs as Record<string, string | number | undefined>;
  const rms = num(specs["RMS Power"]);
  const sens = num(specs["Sensitivity"]);
  const size = parseSpeakerSize(specs);
  const dome = (specs["Tweeter Type"] ?? specs["Dome"] ?? specs["Tweeter"] ?? "").toString().toLowerCase();
  const fr = parseFrequencyResponse(specs);
  const blob = JSON.stringify(specs).toLowerCase();
  const isComponent = blob.includes("component");
  const mountingDepth = num(specs["Mounting Depth"] ?? specs["Depth"]);

  const dailyDriver = clamp100(
    s.reliability * 0.30 + s.buildQuality * 0.20 + s.failureAnalysis * 0.20 +
    s.measuredPerformance * 0.20 + s.abuseTolerance * 0.10,
  );

  let soundQuality = s.buildQuality * 0.28 + s.measuredPerformance * 0.30 +
    s.reliability * 0.20 + s.failureAnalysis * 0.22;
  // Tweeter performance dominates SQ score; material is tiebreaker only.
  const tPerf = tweeterPerformanceScore(p);
  soundQuality += (tPerf.score - 50) * 0.12;
  soundQuality += tweeterMaterialTiebreak(p) * 0.4;
  if (fr.low > 0 && fr.low <= 50 && fr.high >= 22000) soundQuality += 4;
  if (isComponent) soundQuality += 3;
  soundQuality = clamp100(soundQuality);

  let highPowerSPL = s.abuseTolerance * 0.34 + s.measuredPerformance * 0.26 +
    s.failureAnalysis * 0.18 + s.buildQuality * 0.12 + s.reliability * 0.10;
  if (sens >= 91) highPowerSPL += 6;
  if (rms >= 150) highPowerSPL += 4;
  if (size >= 6.5) highPowerSPL += 2;
  highPowerSPL = clamp100(highPowerSPL);

  let factoryReplacement = s.measuredPerformance * 0.30 + s.reliability * 0.30 +
    s.buildQuality * 0.20 + s.failureAnalysis * 0.20;
  if (sens >= 90) factoryReplacement += 8; // critical off factory head-unit power
  else if (sens < 87) factoryReplacement -= 6;
  if (mountingDepth > 0 && mountingDepth <= 2.5) factoryReplacement += 4;
  else if (mountingDepth > 3.2) factoryReplacement -= 4;
  factoryReplacement = clamp100(factoryReplacement);

  let budgetBuild = s.valuePerPerformance * 0.55 + s.reliability * 0.20 +
    s.measuredPerformance * 0.20 + s.failureAnalysis * 0.05;
  if (s.reliability < 45 || s.failureAnalysis < 50) budgetBuild *= 0.75;
  budgetBuild = clamp100(budgetBuild);

  let midbassImpact = s.measuredPerformance * 0.30 + s.abuseTolerance * 0.28 +
    s.buildQuality * 0.20 + s.reliability * 0.12 + s.failureAnalysis * 0.10;
  if (size >= 6.5) midbassImpact += 5;
  if (size < 5.25) midbassImpact -= 8;
  if (fr.low > 0 && fr.low <= 50) midbassImpact += 4;
  if (rms >= 120) midbassImpact += 3;
  midbassImpact = clamp100(midbassImpact);

  return { dailyDriver, soundQuality, highPowerSPL, factoryReplacement, budgetBuild, midbassImpact };
}

const SPEAKER_USE_CASE_LABEL: Record<keyof SpeakerUseCaseScores, string> = {
  dailyDriver: "daily-driver musical use",
  soundQuality: "SQ / sound-quality listening",
  highPowerSPL: "high-power amplified builds",
  factoryReplacement: "factory head-unit drop-in",
  budgetBuild: "budget builds",
  midbassImpact: "kick / door midbass impact",
};

function speakerAdvancedSummary(overall: number, best: keyof SpeakerUseCaseScores, flags: number): string {
  const tier = tierFor(overall);
  const base = tier === "Reference" ? "Reference-grade speaker."
    : tier === "Excellent" ? "Excellent across the board."
    : tier === "Solid" ? "Solid all-rounder."
    : tier === "Average" ? "Average performer — picks its battles."
    : "Below-average — better options exist at this price.";
  return `${base} Best suited for ${SPEAKER_USE_CASE_LABEL[best]}.${flags > 0 ? ` Owner-review trust reduced (${flags} guard flag${flags > 1 ? "s" : ""}).` : ""}`;
}

// ─── Public entry point ──────────────────────────────────────────
export function computeSpeakerAdvancedRating(
  p: Product,
  reviews?: { averageRating: number; reviewCount: number; sources?: { name: string; count?: number; rating?: number }[] },
): SpeakerAdvancedRating {
  if (p.category !== "speaker") {
    const r = computeRatings(p);
    const fb = clamp100((r.reliability.score + r.abuseTolerance.score + r.buildQuality.score) / 3);
    return {
      overall: fb, tier: tierFor(fb),
      signals: { measuredPerformance: fb, reliability: r.reliability.score,
        abuseTolerance: r.abuseTolerance.score, buildQuality: r.buildQuality.score,
        failureAnalysis: 70, installerSignal: 50, verifiedReviewTrust: 50,
        valuePerPerformance: 50 },
      useCases: { dailyDriver: fb, soundQuality: fb, highPowerSPL: fb,
        factoryReplacement: fb, budgetBuild: fb, midbassImpact: fb },
      bestUseCase: "dailyDriver", reviewFlags: [],
      summary: "Non-speaker category — generic fallback.",
    };
  }

  const r = computeRatings(p);
  const measuredPerformance = speakerMeasuredPerformance(p);
  const reliability = r.reliability.score;
  const abuseTolerance = r.abuseTolerance.score;
  const buildQuality = r.buildQuality.score;
  const failureAnalysis = speakerFailureAnalysis(p);
  const installerSignal = speakerInstallerSignal(p);
  const { score: verifiedReviewTrust, flags: reviewFlags } = speakerVerifiedReviewTrust(p, reviews);
  const valuePerPerformance = speakerValuePerPerformance(p, measuredPerformance, reliability);

  // Hybrid weights — sum = 1.00. Owner reviews capped at 8%.
  const overall = clamp100(
    measuredPerformance * 0.18 +
    reliability         * 0.20 +
    abuseTolerance      * 0.14 +
    buildQuality        * 0.14 +
    failureAnalysis     * 0.12 +
    installerSignal     * 0.06 +
    verifiedReviewTrust * 0.08 +
    valuePerPerformance * 0.08,
  );

  const signals = { measuredPerformance, reliability, abuseTolerance, buildQuality,
    failureAnalysis, installerSignal, verifiedReviewTrust, valuePerPerformance };
  const useCases = computeSpeakerUseCases(p, signals);
  const bestUseCase = (Object.entries(useCases)
    .sort(([, a], [, b]) => b - a)[0][0]) as keyof SpeakerUseCaseScores;

  return { overall, tier: tierFor(overall), signals, useCases, bestUseCase,
    reviewFlags, summary: speakerAdvancedSummary(overall, bestUseCase, reviewFlags.length) };
}

// ═════════════════════════════════════════════════════════════════
// SHARED: verified-review trust (anti-manipulation) for any category
// ─────────────────────────────────────────────────────────────────
// Same Bayesian-shrinkage + source-diversity + guard-flag model used
// by the sub/amp/speaker engines, factored out so tweeters and
// alternators get identical rigour instead of a raw average.
// ═════════════════════════════════════════════════════════════════
const SOURCE_TRUST: Record<string, number> = {
  crutchfield: 1.25, sonicelectronix: 1.05, "sonic electronix": 1.05,
  amazon: 0.75, ebay: 0.55, walmart: 0.65,
  manufacturer: 0.45, "manufacturer site": 0.45,
  reddit: 1.0, smd: 1.15, forum: 1.05,
};
function sourceTrustFor(name: string): number {
  const key = name.toLowerCase();
  for (const [k, v] of Object.entries(SOURCE_TRUST)) if (key.includes(k)) return v;
  return 0.9;
}

export function genericVerifiedReviewTrust(
  p: Product,
  reviews?: { averageRating: number; reviewCount: number; sources?: { name: string; count?: number; rating?: number }[] },
): { score: number; flags: string[] } {
  const flags: string[] = [];
  if (!reviews || reviews.reviewCount <= 0) {
    flags.push("no verified reviews — using engine-anchored estimate");
    return { score: fallbackUserReviews(p).score, flags };
  }
  const sources = reviews.sources ?? [];
  const totalCount = reviews.reviewCount;
  const avg = clamp01(reviews.averageRating / 5) * 5;

  let diversityMult = 1.0;
  if (sources.length >= 4) diversityMult = 1.15;
  else if (sources.length >= 2) diversityMult = 1.05;
  else if (sources.length <= 1 && totalCount < 30) {
    flags.push("single source — fake-review risk elevated");
    diversityMult = 0.85;
  }

  // Trust-weighted source average (falls back to the flat average).
  let wSum = 0, w = 0;
  for (const s of sources) {
    const t = sourceTrustFor(s.name) * Math.max(1, s.count ?? 1);
    wSum += (s.rating ?? avg) * t;
    w += t;
  }
  const weightedAvg = w > 0 ? wSum / w : avg;

  if (totalCount < 8) flags.push("very low review volume — low statistical confidence");
  if (brandRep(p.brand) < 50 && avg >= 4.5) flags.push("rating contradicts brand failure-rate evidence");
  const high = sources.filter((s) => (s.rating ?? avg) >= 4.7).length;
  const low = sources.filter((s) => (s.rating ?? avg) <= 3.2).length;
  if (high >= 1 && low >= 1) flags.push("polarized reviews — likely install-dependent");

  const enginePrior = qualityAnchoredPrior(p);
  const effectiveCount = totalCount + flags.length * 25;
  const posterior = (weightedAvg * effectiveCount + enginePrior * PRIOR_WEIGHT) / (effectiveCount + PRIOR_WEIGHT);
  let pct = ramp(posterior, 1.0, 5.0, 0, 100) * diversityMult;

  const forumBlob = [
    p.evidence?.reliability, p.evidence?.abuseTolerance,
    p.evidence?.buildQuality, p.evidence?.userReviews,
  ].filter(Boolean).join(" ");
  const fc = subForumConsensus(forumBlob, "userReviews");
  if (fc) {
    const capped = Math.max(-6, Math.min(3, fc.delta));
    pct += capped;
    flags.push(capped >= 0
      ? `forum consensus boost (+${capped.toFixed(1)}, capped)`
      : `forum consensus drag (${capped.toFixed(1)})`);
  }
  const silence = positiveSilenceBonus(p);
  if (silence) { pct += silence.delta; flags.push(`${silence.label} (+${silence.delta})`); }

  return { score: Math.min(clamp100(pct), qualityCeiling(p)), flags };
}

// Shared evidence-mining helpers so every category reads field reports
// the same way instead of each one inventing its own keyword list.
function evidenceBlob(p: Product): string {
  return [p.evidence?.reliability, p.evidence?.abuseTolerance, p.evidence?.buildQuality]
    .filter(Boolean).join(" ").toLowerCase();
}
function genericFailureAnalysis(p: Product, heavyKeys: string[], minorKeys: string[]): number {
  let score = 80;
  const ev = evidenceBlob(p);
  if (!ev) return score;
  const heavy = heavyKeys.filter((k) => ev.includes(k)).length;
  const minor = minorKeys.filter((k) => ev.includes(k)).length;
  score -= heavy * 9 + minor * 3;
  if (/many users|frequently|commonly|widespread|recurring/.test(ev)) score -= 6;
  if (/no reported failures|bulletproof|never failed|years of abuse/.test(ev)) score += 6;
  return clamp100(score);
}
function genericInstallerSignal(p: Product, posKeys: string[], negKeys: string[]): number {
  const ev = evidenceBlob(p);
  let score = 50 + (brandRep(p.brand) - 50) * 0.35;
  if (!ev) return clamp100(score);
  const pos = posKeys.filter((k) => ev.includes(k)).length;
  const neg = negKeys.filter((k) => ev.includes(k)).length;
  return clamp100(score + pos * 5 - neg * 6);
}

// ═════════════════════════════════════════════════════════════════
// ADVANCED TWEETER RATING SYSTEM
// ─────────────────────────────────────────────────────────────────
// Tweeters live and die on: measured FR flatness / breakup behaviour,
// realistic crossover point vs. Fs, thermal headroom (ferrofluid vs
// dry gap), and whether the dome survives sustained high-pass SPL.
// Material is a tiebreaker only — a well-executed silk dome beats a
// badly-implemented beryllium every time.
// ═════════════════════════════════════════════════════════════════

export interface TweeterUseCaseScores {
  soundQuality: number;      // SQ / imaging accuracy
  highPowerSPL: number;      // pro-audio / horn SPL survival
  dailyDriver: number;       // long-term daily musical use
  factoryReplacement: number; // easy add-on to a factory system
  budgetBuild: number;
  hornLoaded: number;        // pro-audio horn / bullet applications
}

export interface TweeterAdvancedRating {
  overall: number;
  tier: "Reference" | "Excellent" | "Solid" | "Average" | "Weak";
  signals: {
    measuredPerformance: number;
    reliability: number;
    abuseTolerance: number;
    buildQuality: number;
    failureAnalysis: number;
    installerSignal: number;
    verifiedReviewTrust: number;
    valuePerPerformance: number;
  };
  useCases: TweeterUseCaseScores;
  bestUseCase: keyof TweeterUseCaseScores;
  reviewFlags: string[];
  summary: string;
}

function tweeterMeasuredPerformance(p: Product): number {
  const specs = p.specs as Record<string, string | number | undefined>;
  // Reuse the FR-first performance model, then layer tweeter-specific
  // electro-mechanical realism checks on top.
  let score = tweeterPerformanceScore(p).score;

  const sens = num(specs["Sensitivity"]);
  const rms = num(specs["RMS Power"]);
  const peak = num(specs["Peak Power"]);
  const fr = parseFrequencyResponse(specs);
  const type = (specs["Type"] ?? specs["Tweeter Type"] ?? "").toString().toLowerCase();

  // Sensitivity matters, but a tweeter is always level-matched to the
  // midbass, so it's worth less than on a full-range driver.
  if (sens >= 94) score += 6;
  else if (sens >= 91) score += 4;
  else if (sens > 0 && sens < 87) score -= 4;

  // Extension above 20 kHz is a real engineering marker (dome mass,
  // breakup pushed out of band), not just a datasheet flourish.
  if (fr.high >= 30000) score += 5;
  else if (fr.high >= 22000) score += 3;
  else if (fr.high > 0 && fr.high < 18000) score -= 5;

  // Low-end extension sets how low it can safely cross — a tweeter
  // that starts at 1.5 kHz gives the installer real crossover freedom.
  if (fr.low > 0 && fr.low <= 1800) score += 5;
  else if (fr.low >= 4000) score -= 3;

  // Fantasy peak ratings are endemic in this category.
  if (peak > 0 && rms > 0 && peak / rms > 4) score -= 6;

  // Cooling / motor construction realism
  const blob = JSON.stringify(specs).toLowerCase();
  if (blob.includes("ferrofluid")) score += 4;
  if (blob.includes("neodymium")) score += 3;
  if (/compression|horn|bullet/.test(type + blob)) score += 2;

  score += tweeterMaterialTiebreak(p); // tiebreaker only, small magnitude
  return clamp100(score);
}

const TWEETER_HEAVY_FAILURES = [
  "tweeter failure", "blown tweeter", "voice coil burn", "coil burnout",
  "dome collapse", "dome dent", "ferrofluid dried", "ferrofluid dry",
  "lead wire break", "common failure", "recurring failure", "high doa",
];
const TWEETER_MINOR_FAILURES = [
  "harsh", "sibilant", "fatiguing", "bright", "peaky", "shipping damage", "occasional",
];

function computeTweeterUseCases(p: Product, s: TweeterAdvancedRating["signals"]): TweeterUseCaseScores {
  const specs = p.specs as Record<string, string | number | undefined>;
  const sens = num(specs["Sensitivity"]);
  const rms = num(specs["RMS Power"]);
  const fr = parseFrequencyResponse(specs);
  const blob = JSON.stringify(specs).toLowerCase();
  const isPro = /horn|compression|bullet|pro/.test(blob);

  let soundQuality = s.measuredPerformance * 0.36 + s.buildQuality * 0.26 +
    s.reliability * 0.20 + s.failureAnalysis * 0.18;
  if (fr.high >= 25000) soundQuality += 3;
  if (fr.low > 0 && fr.low <= 2000) soundQuality += 3;
  if (isPro) soundQuality -= 6; // pro horns trade linearity for output
  soundQuality = clamp100(soundQuality);

  let highPowerSPL = s.abuseTolerance * 0.36 + s.measuredPerformance * 0.22 +
    s.failureAnalysis * 0.20 + s.buildQuality * 0.12 + s.reliability * 0.10;
  if (sens >= 95) highPowerSPL += 7;
  if (rms >= 80) highPowerSPL += 4;
  if (isPro) highPowerSPL += 5;
  highPowerSPL = clamp100(highPowerSPL);

  const dailyDriver = clamp100(
    s.reliability * 0.30 + s.failureAnalysis * 0.24 + s.buildQuality * 0.20 +
    s.measuredPerformance * 0.18 + s.abuseTolerance * 0.08,
  );

  let factoryReplacement = s.measuredPerformance * 0.26 + s.reliability * 0.30 +
    s.buildQuality * 0.20 + s.failureAnalysis * 0.24;
  if (sens >= 92) factoryReplacement += 6;
  else if (sens > 0 && sens < 88) factoryReplacement -= 5;
  if (blob.includes("surface") || blob.includes("flush")) factoryReplacement += 3;
  factoryReplacement = clamp100(factoryReplacement);

  let budgetBuild = s.valuePerPerformance * 0.55 + s.reliability * 0.20 +
    s.measuredPerformance * 0.20 + s.failureAnalysis * 0.05;
  if (s.reliability < 45 || s.failureAnalysis < 50) budgetBuild *= 0.75;
  budgetBuild = clamp100(budgetBuild);

  let hornLoaded = s.abuseTolerance * 0.34 + s.failureAnalysis * 0.22 +
    s.buildQuality * 0.18 + s.measuredPerformance * 0.16 + s.reliability * 0.10;
  hornLoaded += isPro ? 10 : -12;
  if (sens >= 100) hornLoaded += 6;
  hornLoaded = clamp100(hornLoaded);

  return { soundQuality, highPowerSPL, dailyDriver, factoryReplacement, budgetBuild, hornLoaded };
}

const TWEETER_USE_CASE_LABEL: Record<keyof TweeterUseCaseScores, string> = {
  soundQuality: "SQ / imaging-focused builds",
  highPowerSPL: "high-power amplified builds",
  dailyDriver: "daily-driver musical use",
  factoryReplacement: "factory-system upgrades",
  budgetBuild: "budget builds",
  hornLoaded: "pro-audio horn / bullet setups",
};

export function computeTweeterAdvancedRating(
  p: Product,
  reviews?: { averageRating: number; reviewCount: number; sources?: { name: string; count?: number; rating?: number }[] },
): TweeterAdvancedRating {
  const r = computeRatings(p);
  if (p.category !== "tweeter") {
    const fb = clamp100((r.reliability.score + r.abuseTolerance.score + r.buildQuality.score) / 3);
    return {
      overall: fb, tier: tierFor(fb),
      signals: { measuredPerformance: fb, reliability: r.reliability.score,
        abuseTolerance: r.abuseTolerance.score, buildQuality: r.buildQuality.score,
        failureAnalysis: 70, installerSignal: 50, verifiedReviewTrust: 50, valuePerPerformance: 50 },
      useCases: { soundQuality: fb, highPowerSPL: fb, dailyDriver: fb,
        factoryReplacement: fb, budgetBuild: fb, hornLoaded: fb },
      bestUseCase: "dailyDriver", reviewFlags: [],
      summary: "Non-tweeter category — generic fallback.",
    };
  }

  const measuredPerformance = tweeterMeasuredPerformance(p);
  const reliability = r.reliability.score;
  const abuseTolerance = r.abuseTolerance.score;
  const buildQuality = r.buildQuality.score;
  const failureAnalysis = genericFailureAnalysis(p, TWEETER_HEAVY_FAILURES, TWEETER_MINOR_FAILURES);
  const installerSignal = genericInstallerSignal(p,
    ["installer favorite", "shop favorite", "sq winner", "competition proven",
      "reference tweeter", "tonally accurate", "imaging", "staging", "smooth top end"],
    ["installers avoid", "harsh tweeter", "fatiguing", "sibilant", "overrated",
      "rebadge", "inflated specs", "doa", "warranty queen"],
  );
  const { score: verifiedReviewTrust, flags: reviewFlags } = genericVerifiedReviewTrust(p, reviews);

  // Value: tweeters have an enormous price spread ($20 pro horns to
  // $500 beryllium), so value is measured against performance delivered.
  let valuePerPerformance = 50;
  if (p.price > 0) {
    if (p.price <= 60 && measuredPerformance >= 60) valuePerPerformance += 18;
    else if (p.price <= 150 && measuredPerformance >= 70) valuePerPerformance += 14;
    else if (p.price <= 300 && measuredPerformance >= 78) valuePerPerformance += 9;
    else if (p.price > 300 && measuredPerformance < 80) valuePerPerformance -= 10;
    valuePerPerformance += (measuredPerformance - 60) * 0.30;
    if (reliability < 50) valuePerPerformance *= 0.8;
  }
  valuePerPerformance = clamp100(valuePerPerformance);

  const overall = clamp100(
    measuredPerformance * 0.22 +
    reliability         * 0.18 +
    abuseTolerance      * 0.12 +
    buildQuality        * 0.14 +
    failureAnalysis     * 0.12 +
    installerSignal     * 0.06 +
    verifiedReviewTrust * 0.08 +
    valuePerPerformance * 0.08,
  );

  const signals = { measuredPerformance, reliability, abuseTolerance, buildQuality,
    failureAnalysis, installerSignal, verifiedReviewTrust, valuePerPerformance };
  const useCases = computeTweeterUseCases(p, signals);
  const bestUseCase = (Object.entries(useCases).sort(([, a], [, b]) => b - a)[0][0]) as keyof TweeterUseCaseScores;
  const tier = tierFor(overall);
  const base = tier === "Reference" ? "Reference-grade tweeter."
    : tier === "Excellent" ? "Excellent across the board."
    : tier === "Solid" ? "Solid all-rounder."
    : tier === "Average" ? "Average performer — picks its battles."
    : "Below-average — better options exist at this price.";

  return {
    overall, tier, signals, useCases, bestUseCase, reviewFlags,
    summary: `${base} Best suited for ${TWEETER_USE_CASE_LABEL[bestUseCase]}.${
      reviewFlags.length > 0 ? ` Owner-review trust reduced (${reviewFlags.length} guard flag${reviewFlags.length > 1 ? "s" : ""}).` : ""}`,
  };
}

// ═════════════════════════════════════════════════════════════════
// ADVANCED ALTERNATOR RATING SYSTEM
// ─────────────────────────────────────────────────────────────────
// What actually matters on a high-output alternator: idle output (not
// peak — peak is a dyno number at 6000 rpm), thermal/duty-cycle
// survival, rectifier & regulator quality, bearing life, hairpin vs
// round-wire stator, and warranty behaviour of the builder.
// ═════════════════════════════════════════════════════════════════

export interface AlternatorUseCaseScores {
  dailyDriver: number;      // street car, long idle sessions
  splCompetition: number;   // burst-current demand
  highIdleDemand: number;   // stereo-on-while-parked / show use
  towOrWork: number;        // sustained high-load, heat soak
  budgetBuild: number;
  bigBankCharging: number;  // charging large lithium banks
}

export interface AlternatorAdvancedRating {
  overall: number;
  tier: "Reference" | "Excellent" | "Solid" | "Average" | "Weak";
  signals: {
    measuredPerformance: number;
    reliability: number;
    abuseTolerance: number;
    buildQuality: number;
    failureAnalysis: number;
    installerSignal: number;
    verifiedReviewTrust: number;
    valuePerPerformance: number;
  };
  useCases: AlternatorUseCaseScores;
  bestUseCase: keyof AlternatorUseCaseScores;
  reviewFlags: string[];
  summary: string;
}

function altOutputs(specs: Record<string, string | number | undefined>) {
  const max = num(specs["Max Output"] ?? specs["Output"] ?? specs["Rated Output"]);
  const idle = num(specs["Idle Output"] ?? specs["Output at Idle"]);
  return { max, idle };
}

function alternatorMeasuredPerformance(p: Product): number {
  const specs = p.specs as Record<string, string | number | undefined>;
  const { max, idle } = altOutputs(specs);
  const blob = JSON.stringify(specs).toLowerCase();
  let score = 45;

  // Idle output is the single most useful number in car audio — an amp
  // draws its worst current at a red light, not at 5000 rpm.
  if (idle > 0) {
    score += Math.min(24, 24 * (1 - Math.exp(-idle / 130)));
  }
  // Peak output still counts, but at lower weight.
  if (max > 0) {
    score += Math.min(14, 14 * (1 - Math.exp(-max / 260)));
  }
  // Idle-to-peak ratio exposes builders who quote a big dyno number on
  // a stator that makes almost nothing under 1500 rpm.
  if (max > 0 && idle > 0) {
    const ratio = idle / max;
    if (ratio >= 0.55) score += 10;
    else if (ratio >= 0.45) score += 6;
    else if (ratio < 0.3) score -= 8;
  } else if (max > 0 && idle <= 0) {
    score -= 4; // no published idle figure is itself a signal
  }

  // Construction markers with real electrical consequences.
  if (blob.includes("hairpin")) score += 6;
  if (/6[- ]?phase|six[- ]?phase/.test(blob)) score += 5;
  if (blob.includes("billet")) score += 4;
  if (blob.includes("brushless")) score += 5;
  if (/dual .*(rectifier|diode)|high[- ]?amp rectifier|avalanche/.test(blob)) score += 4;
  if (blob.includes("external regulator")) score += 2;
  if (/remanufactur|rebuilt|clutch pulley delete/.test(blob)) score -= 5;

  return clamp100(score);
}

const ALT_HEAVY_FAILURES = [
  "rectifier failure", "diode failure", "regulator failure", "burned stator",
  "bearing failure", "bearing whine", "melted", "overheat", "smoked",
  "common failure", "recurring failure", "high doa", "warranty denied",
];
const ALT_MINOR_FAILURES = [
  "whine", "belt slip", "noisy", "pulley", "shipping damage", "occasional", "core charge",
];

function computeAlternatorUseCases(p: Product, s: AlternatorAdvancedRating["signals"]): AlternatorUseCaseScores {
  const specs = p.specs as Record<string, string | number | undefined>;
  const { max, idle } = altOutputs(specs);
  const blob = JSON.stringify(specs).toLowerCase();
  const idleRatio = max > 0 && idle > 0 ? idle / max : 0;

  let dailyDriver = s.reliability * 0.32 + s.failureAnalysis * 0.24 +
    s.buildQuality * 0.20 + s.measuredPerformance * 0.16 + s.abuseTolerance * 0.08;
  if (idleRatio >= 0.5) dailyDriver += 5;
  if (/1 year|no warranty/.test(blob)) dailyDriver -= 3;
  dailyDriver = clamp100(dailyDriver);

  let splCompetition = s.measuredPerformance * 0.32 + s.abuseTolerance * 0.30 +
    s.buildQuality * 0.16 + s.failureAnalysis * 0.12 + s.reliability * 0.10;
  if (max >= 320) splCompetition += 7;
  else if (max >= 250) splCompetition += 4;
  else if (max > 0 && max < 180) splCompetition -= 8;
  splCompetition = clamp100(splCompetition);

  let highIdleDemand = s.measuredPerformance * 0.30 + s.abuseTolerance * 0.24 +
    s.reliability * 0.22 + s.buildQuality * 0.14 + s.failureAnalysis * 0.10;
  if (idle >= 180) highIdleDemand += 10;
  else if (idle >= 130) highIdleDemand += 6;
  else if (idle > 0 && idle < 80) highIdleDemand -= 8;
  if (blob.includes("hairpin")) highIdleDemand += 3;
  highIdleDemand = clamp100(highIdleDemand);

  let towOrWork = s.abuseTolerance * 0.30 + s.reliability * 0.26 +
    s.failureAnalysis * 0.22 + s.buildQuality * 0.14 + s.measuredPerformance * 0.08;
  if (/dual .*(fan|rectifier)|thermal|heat sink|cooling/.test(blob)) towOrWork += 5;
  if (blob.includes("lifetime")) towOrWork += 4;
  towOrWork = clamp100(towOrWork);

  let budgetBuild = s.valuePerPerformance * 0.55 + s.reliability * 0.22 +
    s.measuredPerformance * 0.18 + s.failureAnalysis * 0.05;
  if (s.reliability < 45 || s.failureAnalysis < 50) budgetBuild *= 0.75;
  budgetBuild = clamp100(budgetBuild);

  let bigBankCharging = s.measuredPerformance * 0.30 + s.abuseTolerance * 0.24 +
    s.buildQuality * 0.18 + s.reliability * 0.18 + s.failureAnalysis * 0.10;
  // Lithium banks demand sustained current and a regulator that holds
  // voltage — a stock-voltage 14.0 V unit under-charges LiFePO4 packs.
  const voltage = num(specs["Voltage"] ?? specs["Charging Voltage"]);
  if (voltage >= 14.4) bigBankCharging += 6;
  else if (voltage > 0 && voltage < 14.0) bigBankCharging -= 6;
  if (blob.includes("external regulator") || blob.includes("adjustable")) bigBankCharging += 4;
  if (idle >= 150) bigBankCharging += 5;
  bigBankCharging = clamp100(bigBankCharging);

  return { dailyDriver, splCompetition, highIdleDemand, towOrWork, budgetBuild, bigBankCharging };
}

const ALT_USE_CASE_LABEL: Record<keyof AlternatorUseCaseScores, string> = {
  dailyDriver: "daily-driven street builds",
  splCompetition: "SPL competition current demand",
  highIdleDemand: "long idle / parked-and-playing sessions",
  towOrWork: "tow rigs and sustained high-load use",
  budgetBuild: "budget builds",
  bigBankCharging: "charging large lithium banks",
};

export function computeAlternatorAdvancedRating(
  p: Product,
  reviews?: { averageRating: number; reviewCount: number; sources?: { name: string; count?: number; rating?: number }[] },
): AlternatorAdvancedRating {
  const r = computeRatings(p);
  if (p.category !== "alternator") {
    const fb = clamp100((r.reliability.score + r.abuseTolerance.score + r.buildQuality.score) / 3);
    return {
      overall: fb, tier: tierFor(fb),
      signals: { measuredPerformance: fb, reliability: r.reliability.score,
        abuseTolerance: r.abuseTolerance.score, buildQuality: r.buildQuality.score,
        failureAnalysis: 70, installerSignal: 50, verifiedReviewTrust: 50, valuePerPerformance: 50 },
      useCases: { dailyDriver: fb, splCompetition: fb, highIdleDemand: fb,
        towOrWork: fb, budgetBuild: fb, bigBankCharging: fb },
      bestUseCase: "dailyDriver", reviewFlags: [],
      summary: "Non-alternator category — generic fallback.",
    };
  }

  const specs = p.specs as Record<string, string | number | undefined>;
  const measuredPerformance = alternatorMeasuredPerformance(p);
  const reliability = r.reliability.score;
  const abuseTolerance = r.abuseTolerance.score;
  const buildQuality = r.buildQuality.score;
  const failureAnalysis = genericFailureAnalysis(p, ALT_HEAVY_FAILURES, ALT_MINOR_FAILURES);
  const installerSignal = genericInstallerSignal(p,
    ["installer favorite", "shop favorite", "go-to alternator", "competition proven",
      "lifetime warranty honored", "honors warranty", "great support", "highly recommended by"],
    ["installers avoid", "warranty nightmare", "no support", "core charge issues",
      "inflated rating", "overrated", "rebadge", "doa", "warranty queen"],
  );
  const { score: verifiedReviewTrust, flags: reviewFlags } = genericVerifiedReviewTrust(p, reviews);

  // Value is dollars-per-idle-amp, not dollars-per-peak-amp.
  const { max, idle } = altOutputs(specs);
  const usableAmps = idle > 0 ? idle : max * 0.4;
  let valuePerPerformance = 50;
  if (p.price > 0 && usableAmps > 0) {
    const dollarsPerIdleAmp = p.price / usableAmps;
    if (dollarsPerIdleAmp <= 2.5) valuePerPerformance += 20;
    else if (dollarsPerIdleAmp <= 3.5) valuePerPerformance += 14;
    else if (dollarsPerIdleAmp <= 5) valuePerPerformance += 7;
    else if (dollarsPerIdleAmp > 8) valuePerPerformance -= 12;
    valuePerPerformance += (measuredPerformance - 60) * 0.25;
    if (reliability < 55) valuePerPerformance *= 0.8; // a cheap alt that dies has no value
  }
  valuePerPerformance = clamp100(valuePerPerformance);

  // Reliability is weighted highest here: an alternator failure strands
  // the vehicle, unlike a blown sub.
  const overall = clamp100(
    measuredPerformance * 0.18 +
    reliability         * 0.24 +
    abuseTolerance      * 0.14 +
    buildQuality        * 0.14 +
    failureAnalysis     * 0.12 +
    installerSignal     * 0.06 +
    verifiedReviewTrust * 0.06 +
    valuePerPerformance * 0.06,
  );

  const signals = { measuredPerformance, reliability, abuseTolerance, buildQuality,
    failureAnalysis, installerSignal, verifiedReviewTrust, valuePerPerformance };
  const useCases = computeAlternatorUseCases(p, signals);
  const bestUseCase = (Object.entries(useCases).sort(([, a], [, b]) => b - a)[0][0]) as keyof AlternatorUseCaseScores;
  const tier = tierFor(overall);
  const base = tier === "Reference" ? "Reference-grade alternator."
    : tier === "Excellent" ? "Excellent across the board."
    : tier === "Solid" ? "Solid all-rounder."
    : tier === "Average" ? "Average performer — picks its battles."
    : "Below-average — better options exist at this price.";

  return {
    overall, tier, signals, useCases, bestUseCase, reviewFlags,
    summary: `${base} Best suited for ${ALT_USE_CASE_LABEL[bestUseCase]}.${
      reviewFlags.length > 0 ? ` Owner-review trust reduced (${reviewFlags.length} guard flag${reviewFlags.length > 1 ? "s" : ""}).` : ""}`,
  };
}

// ═════════════════════════════════════════════════════════════════
// UNIFIED ADVANCED RATING DISPATCHER
// ─────────────────────────────────────────────────────────────────
// Every category now has a dedicated 8-signal hybrid model. This is
// the single entry point the UI should use so no product ever falls
// back to a naive four-number average.
// ═════════════════════════════════════════════════════════════════

export interface AnyAdvancedRating {
  overall: number;
  tier: "Reference" | "Excellent" | "Solid" | "Average" | "Weak";
  signals: SubwooferAdvancedRating["signals"];
  useCases: Record<string, number>;
  bestUseCase: string;
  reviewFlags: string[];
  summary: string;
}

type CategoryAdvancedRating =
  | SubwooferAdvancedRating
  | AmplifierAdvancedRating
  | SpeakerAdvancedRating
  | TweeterAdvancedRating
  | AlternatorAdvancedRating;

function widen(r: CategoryAdvancedRating): AnyAdvancedRating {
  return {
    overall: r.overall,
    tier: r.tier,
    signals: r.signals,
    useCases: { ...r.useCases },
    bestUseCase: r.bestUseCase as string,
    reviewFlags: r.reviewFlags,
    summary: r.summary,
  };
}

export function computeAdvancedRating(
  p: Product,
  reviews?: { averageRating: number; reviewCount: number; sources?: { name: string; count?: number; rating?: number }[] },
): AnyAdvancedRating {
  switch (p.category) {
    case "subwoofer":  return widen(computeSubwooferAdvancedRating(p, reviews));
    case "amplifier":  return widen(computeAmplifierAdvancedRating(p, reviews));
    case "speaker":    return widen(computeSpeakerAdvancedRating(p, reviews));
    case "tweeter":    return widen(computeTweeterAdvancedRating(p, reviews));
    case "alternator": return widen(computeAlternatorAdvancedRating(p, reviews));
    default: {
      const r = computeRatings(p);
      const fb = clamp100((r.reliability.score + r.abuseTolerance.score + r.buildQuality.score) / 3);
      return {
        overall: fb, tier: tierFor(fb),
        signals: { measuredPerformance: fb, reliability: r.reliability.score,
          abuseTolerance: r.abuseTolerance.score, buildQuality: r.buildQuality.score,
          failureAnalysis: 70, installerSignal: 50, verifiedReviewTrust: 50, valuePerPerformance: 50 },
        useCases: { dailyDriver: fb }, bestUseCase: "dailyDriver",
        reviewFlags: [], summary: "Generic rating.",
      };
    }
  }
}

/** Convenience: the 0-100 overall score for any product. */
export function advancedOverall(p: Product): number {
  return computeAdvancedRating(p).overall;
}
